import PageHeader from './PageHeader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import i18n from '../i18n/config';
import ApiService, { TemplateNotFoundError, TemplatePermissionError } from '../services/apiService';
import { MeetingType, TemplatePreview, TemplateComponent, SmartTemplateSchema, SmartTemplateField, TemplateDraft, Recording, ProcessingStatus, TemplateSuggestion, SectionLengthLimits } from '../types';
import { getTemplateRef, templateEditorPath } from '../utils/templateRef';
import TemplateContentEditor from './TemplateContentEditor';
import { useFeature } from '../hooks/useFeature';
import { markdownToHtml } from '../utils/markdownToHtml';
import '../styles/SummaryEditor.css';
import '../styles/TemplatePicker.css';
import { logger } from '../utils/logger';

const FIELD_CODE_PATTERN = /^[A-Za-z0-9._-]+$/;

const normalizeSmartTemplateSchema = (schema?: SmartTemplateSchema | null): SmartTemplateSchema => ({
    version: Number.isFinite(Number(schema?.version)) ? Number(schema?.version) : 1,
    syntax: 'field_markers',
    fields: Array.isArray(schema?.fields)
        ? schema!.fields.map((field) => ({
            code: field.code || '',
            label: field.label || '',
            description: field.description || '',
            required: Boolean(field.required),
            multiline: field.multiline !== false,
            exportKey: field.exportKey || '',
        }))
        : [],
});

const isEmptySmartField = (field: SmartTemplateField) =>
    !field.code.trim() && !field.label.trim() && !(field.description || '').trim() && !(field.exportKey || '').trim();

const buildSmartTemplateSchemaPayload = (schema: SmartTemplateSchema): SmartTemplateSchema | null => {
    const fields = schema.fields
        .map((field) => ({
            code: field.code.trim(),
            label: field.label.trim(),
            description: field.description?.trim() || '',
            required: Boolean(field.required),
            multiline: Boolean(field.multiline),
            exportKey: field.exportKey?.trim() || undefined,
        }))
        .filter((field) => !isEmptySmartField(field));

    if (fields.length === 0) {
        return null;
    }

    return {
        version: 1,
        syntax: 'field_markers',
        fields,
    };
};

const getSmartTemplateFieldErrors = (schema: SmartTemplateSchema): Record<number, string[]> => {
    const errors: Record<number, string[]> = {};
    const codeCounts = new Map<string, number>();

    schema.fields.forEach((field) => {
        if (isEmptySmartField(field)) {
            return;
        }

        const code = field.code.trim();
        if (!code) {
            return;
        }

        codeCounts.set(code, (codeCounts.get(code) || 0) + 1);
    });

    schema.fields.forEach((field, index) => {
        if (isEmptySmartField(field)) {
            return;
        }

        const fieldErrors: string[] = [];
        const code = field.code.trim();

        if (!code) {
            fieldErrors.push(i18n.t('templatesManager.validation.codeRequired'));
        } else {
            if (!FIELD_CODE_PATTERN.test(code)) {
                fieldErrors.push(i18n.t('templatesManager.validation.codePattern'));
            }
            if ((codeCounts.get(code) || 0) > 1) {
                fieldErrors.push(i18n.t('templatesManager.validation.codeUnique'));
            }
        }

        if (fieldErrors.length > 0) {
            errors[index] = fieldErrors;
        }
    });

    return errors;
};

const hasSmartTemplateValidationErrors = (schema: SmartTemplateSchema) =>
    Object.values(getSmartTemplateFieldErrors(schema)).some((fieldErrors) => fieldErrors.length > 0);

// Verslaglengte (additional_settings.lengte)

type ReportLengthMode = 'auto' | 'profile' | 'fixed' | 'percent';
type ReportLengthProfile = 'beknopt' | 'normaal' | 'uitgebreid';

interface ReportLengthState {
    mode: ReportLengthMode;
    profile: ReportLengthProfile;
    fixedMin: string;
    fixedMax: string;
    percent: number;
}

const LENGTH_PROFILE_ALIASES: Record<string, ReportLengthProfile> = {
    beknopt: 'beknopt', kort: 'beknopt', compact: 'beknopt',
    normaal: 'normaal', gemiddeld: 'normaal', standaard: 'normaal',
    uitgebreid: 'uitgebreid', lang: 'uitgebreid', uitvoerig: 'uitgebreid', verhalend: 'uitgebreid', gedetailleerd: 'uitgebreid',
};

const DEFAULT_REPORT_LENGTH_STATE: ReportLengthState = {
    mode: 'auto',
    profile: 'normaal',
    fixedMin: '',
    fixedMax: '600',
    percent: 15,
};

const clampPercent = (value: number) => Math.min(100, Math.max(1, Math.round(value)));

const parseReportLength = (value: string): ReportLengthState => {
    const v = (value || '').trim().toLowerCase().replace(',', '.');
    if (!v) return DEFAULT_REPORT_LENGTH_STATE;

    const profile = LENGTH_PROFILE_ALIASES[v];
    if (profile) return { ...DEFAULT_REPORT_LENGTH_STATE, mode: 'profile', profile };

    const percentRange = v.match(/^(\d+(?:\.\d+)?)\s*(?:-|tot)\s*(\d+(?:\.\d+)?)\s*%$/);
    if (percentRange) return { ...DEFAULT_REPORT_LENGTH_STATE, mode: 'percent', percent: clampPercent(Number(percentRange[2])) };

    const percentSingle = v.match(/^(\d+(?:\.\d+)?)\s*%$/);
    if (percentSingle) return { ...DEFAULT_REPORT_LENGTH_STATE, mode: 'percent', percent: clampPercent(Number(percentSingle[1])) };

    const fixedRange = v.match(/^(\d+)\s*(?:-|tot)\s*(\d+)$/);
    if (fixedRange) return { ...DEFAULT_REPORT_LENGTH_STATE, mode: 'fixed', fixedMin: fixedRange[1], fixedMax: fixedRange[2] };

    if (/^\d+$/.test(v)) return { ...DEFAULT_REPORT_LENGTH_STATE, mode: 'fixed', fixedMin: '', fixedMax: v };

    const fraction = Number(v);
    if (Number.isFinite(fraction) && fraction > 0 && fraction <= 1) {
        return { ...DEFAULT_REPORT_LENGTH_STATE, mode: 'percent', percent: clampPercent(fraction * 100) };
    }

    // Onbekende (vrije-tekst)waarde: toon als Automatisch
    return DEFAULT_REPORT_LENGTH_STATE;
};

const serializeReportLength = (state: ReportLengthState): string => {
    switch (state.mode) {
        case 'auto':
            return '';
        case 'profile':
            return state.profile;
        case 'fixed': {
            const min = state.fixedMin.trim();
            const max = state.fixedMax.trim();
            if (min && max) return `${min}-${max}`;
            return max || min || '';
        }
        case 'percent':
            return `${state.percent}%`;
    }
};

const ReportLengthControl: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
    const { t } = useTranslation();
    const [state, setState] = useState<ReportLengthState>(() => parseReportLength(value));
    const lastEmittedRef = useRef(value);

    useEffect(() => {
        // Alleen hersynchroniseren bij externe wijzigingen (template wisselen, undo/redo)
        if (value !== lastEmittedRef.current) {
            lastEmittedRef.current = value;
            setState(parseReportLength(value));
        }
    }, [value]);

    const update = (patch: Partial<ReportLengthState>) => {
        const next = { ...state, ...patch };
        setState(next);
        const serialized = serializeReportLength(next);
        if (serialized !== lastEmittedRef.current) {
            lastEmittedRef.current = serialized;
            onChange(serialized);
        }
    };

    const fixedError = (() => {
        if (state.mode !== 'fixed') return '';
        const min = state.fixedMin.trim();
        const max = state.fixedMax.trim();
        for (const raw of [min, max]) {
            if (!raw) continue;
            const num = Number(raw);
            if (!Number.isInteger(num) || num < 50 || num > 4000) return t('templatesManager.update.lengthFixedRangeError');
        }
        if (min && max && Number(min) >= Number(max)) return t('templatesManager.update.lengthFixedRangeError');
        return '';
    })();

    const profileOptions: { key: ReportLengthProfile; label: string; hint: string }[] = [
        { key: 'beknopt', label: t('templatesManager.update.lengthProfileBeknopt'), hint: t('templatesManager.update.lengthProfileHintBeknopt') },
        { key: 'normaal', label: t('templatesManager.update.lengthProfileNormaal'), hint: t('templatesManager.update.lengthProfileHintNormaal') },
        { key: 'uitgebreid', label: t('templatesManager.update.lengthProfileUitgebreid'), hint: t('templatesManager.update.lengthProfileHintUitgebreid') },
    ];

    const exampleWords = Math.round(9000 * state.percent / 100);

    return (
        <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.lengthLabel')}</label>
            <select
                value={state.mode}
                onChange={(e) => update({ mode: e.target.value as ReportLengthMode })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
            >
                <option value="auto">{t('templatesManager.update.lengthModeAuto')}</option>
                <option value="profile">{t('templatesManager.update.lengthModeProfile')}</option>
                <option value="fixed">{t('templatesManager.update.lengthModeFixed')}</option>
                <option value="percent">{t('templatesManager.update.lengthModePercent')}</option>
            </select>

            {state.mode === 'profile' && (
                <div className="mt-2">
                    <div className="grid grid-cols-3 gap-1">
                        {profileOptions.map((option) => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => update({ profile: option.key })}
                                className={`px-2 py-1.5 text-xs rounded-lg border transition-colors ${state.profile === option.key
                                    ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {profileOptions.find((option) => option.key === state.profile)?.hint}
                    </p>
                </div>
            )}

            {state.mode === 'fixed' && (
                <div className="mt-2">
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={50}
                            max={4000}
                            value={state.fixedMin}
                            onChange={(e) => update({ fixedMin: e.target.value })}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder={t('templatesManager.update.lengthFixedMinPlaceholder')}
                        />
                        <span className="text-sm text-gray-500">{t('templatesManager.update.lengthFixedTo')}</span>
                        <input
                            type="number"
                            min={50}
                            max={4000}
                            value={state.fixedMax}
                            onChange={(e) => update({ fixedMax: e.target.value })}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder={t('templatesManager.update.lengthFixedMaxPlaceholder')}
                        />
                        <span className="text-sm text-gray-500">{t('templatesManager.update.lengthFixedWords')}</span>
                    </div>
                    {fixedError ? (
                        <p className="text-xs text-red-600 mt-1">{fixedError}</p>
                    ) : (
                        <p className="text-xs text-gray-500 mt-1">{t('templatesManager.update.lengthFixedHint')}</p>
                    )}
                </div>
            )}

            {state.mode === 'percent' && (
                <div className="mt-2">
                    <div className="flex items-center gap-3">
                        <input
                            type="range"
                            min={5}
                            max={50}
                            value={Math.min(50, Math.max(5, state.percent))}
                            onChange={(e) => update({ percent: Number(e.target.value) })}
                            className="flex-1 accent-primary-600"
                        />
                        <span className="text-sm text-gray-700 w-12 text-right tabular-nums">{state.percent}%</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        {t('templatesManager.update.lengthPercentExample', { words: exampleWords.toLocaleString(i18n.language) })}
                    </p>
                </div>
            )}

            <p className="text-xs text-gray-400 mt-2">{t('templatesManager.update.lengthSectionHint')}</p>
        </div>
    );
};

const TemplatesManager: React.FC = () => {
    const { t } = useTranslation();
    const { templateId: urlTemplateId } = useParams<{ templateId: string }>();
    const navigate = useNavigate();
    const api = new ApiService();
    const showSmartOverlay = useFeature('smart_templates');
    const showTemplateSuggestions = useFeature('template_suggestions');
    const [templates, setTemplates] = useState<MeetingType[]>([]);
    const [listFilter, setListFilter] = useState('');
    const [, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [generateFeedback, setGenerateFeedback] = useState('');
    const [requirements, setRequirements] = useState('');
    const [updateFeedback, setUpdateFeedback] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    // Sleutel (uuid) voor de template-endpoints van het geopende template. Begint
    // bij de uuid uit de lijst en volgt daarna de uuid uit de responses. Een ref,
    // zodat opeenvolgende awaits in één handler meteen de nieuwe waarde gebruiken.
    const activeTemplateKeyRef = useRef<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [mode, setMode] = useState<'none' | 'generate' | 'preview' | 'update' | 'editMetadata'>('none');
    const [, setGenerateQA] = useState({ goal: '', sections: '', tone: '', terms: '' });
    const [, setActiveGenerateQuestions] = useState<string[]>([]);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');

    // Metadata editing state
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [isSavingMetadata, setIsSavingMetadata] = useState(false);

    // Update flow state - editor + AI side-by-side
    const [editableTemplateContent, setEditableTemplateContent] = useState('');
    const [editableTemplateSchema, setEditableTemplateSchema] = useState<SmartTemplateSchema>(normalizeSmartTemplateSchema());
    const [editableTemplateTitle, setEditableTemplateTitle] = useState('');
    const [editableTemplateDescription, setEditableTemplateDescription] = useState('');
    const [editAdditionalSettings, setEditAdditionalSettings] = useState({ goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' });
    const [isAdditionalSettingsOpen, setIsAdditionalSettingsOpen] = useState(false);

    // Draft (concept) state
    const [currentDraft, setCurrentDraft] = useState<TemplateDraft | null>(null);
    const [isDraftDirty, setIsDraftDirty] = useState(false);
    // Door de backend herkende sectielimieten (uit het laatst opgeslagen/geladen concept)
    const [sectionLengthLimits, setSectionLengthLimits] = useState<SectionLengthLimits | null>(null);

    // Undo/redo state for template editing
    type AdditionalSettingsState = { goal: string; perspective: string; audience: string; style: string; tense: string; structure: string; length: string };
    type EditorSnapshot = { content: string; title: string; description: string; schema: SmartTemplateSchema; additionalSettings: AdditionalSettingsState };
    const historyRef = useRef<EditorSnapshot[]>([]);
    const historyIndexRef = useRef(-1);
    const baselineSnapshotRef = useRef<EditorSnapshot | null>(null);
    const isApplyingHistoryRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingSnapshotRef = useRef<EditorSnapshot | null>(null);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [isDiscardingDraft, setIsDiscardingDraft] = useState(false);
    const [isComparingDiscard, setIsComparingDiscard] = useState(false);
    const [liveCompareSnapshot, setLiveCompareSnapshot] = useState<MeetingType | null>(null);

    // Copy-dialog state for default/org templates
    const [copyDialog, setCopyDialog] = useState<{ template: MeetingType; title: string } | null>(null);
    const [isCopying, setIsCopying] = useState(false);

    // In-app confirm dialog (vervangt window.confirm)
    type ConfirmDialogState = {
        title: string;
        message: string;
        confirmLabel: string;
        confirmVariant?: 'primary' | 'danger';
        onConfirm: () => void | Promise<void>;
    };
    const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);

    // Template testen state
    const FICTIVE_OPTION_VALUE = '__fictive__';
    const [recentFiles, setRecentFiles] = useState<Recording[]>([]);
    const [recentFilesLoading, setRecentFilesLoading] = useState(false);
    const [selectedTestOption, setSelectedTestOption] = useState<string>('');
    const [testSourceOpen, setTestSourceOpen] = useState(false);
    const testSourceDropdownRef = useRef<HTMLDivElement>(null);
    const [isGeneratingFictive, setIsGeneratingFictive] = useState(false);
    const [isStreamingTest, setIsStreamingTest] = useState(false);
    const [cachedFictiveTranscript, setCachedFictiveTranscript] = useState<string | null>(null);
    const [fictiveProgressStep, setFictiveProgressStep] = useState(0);
    const [fictiveProgress, setFictiveProgress] = useState(0);

    const FICTIVE_LOADING_STEPS = [
        t('templatesManager.fictive.loadingSteps.scenario'),
        t('templatesManager.fictive.loadingSteps.participants'),
        t('templatesManager.fictive.loadingSteps.agenda'),
        t('templatesManager.fictive.loadingSteps.discussion'),
        t('templatesManager.fictive.loadingSteps.decisions'),
        t('templatesManager.fictive.loadingSteps.transcript'),
        t('templatesManager.fictive.loadingSteps.finishing'),
    ];
    const [isTesting, setIsTesting] = useState(false);
    const [testStatusMessage, setTestStatusMessage] = useState('');
    const [testContent, setTestContent] = useState('');
    const [testError, setTestError] = useState('');

    // Preview state
    const [previewData, setPreviewData] = useState<TemplatePreview | null>(null);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
    const [savedFeedbackForPreview, setSavedFeedbackForPreview] = useState('');
    const [savedRequirementsForPreview, setSavedRequirementsForPreview] = useState('');
    const [previewStep, setPreviewStep] = useState<1 | 2>(1); // Step 1: content, Step 2: metadata/style feedback
    const [editablePreviewContent, setEditablePreviewContent] = useState('');
    const [previewFeedback, setPreviewFeedback] = useState({
        goal: '',
        perspective: '',
        audience: '',
        style: '',
        tense: '',
        structure: '',
        length: ''
    });
    const [exampleDocument, setExampleDocument] = useState<File | null>(null);
    const [previewTitle, setPreviewTitle] = useState('');
    const [previewDescription, setPreviewDescription] = useState('');
    const [previewProgressStep, setPreviewProgressStep] = useState(0);
    const [previewProgress, setPreviewProgress] = useState(0);
    const [editablePreviewSchema, setEditablePreviewSchema] = useState<SmartTemplateSchema>(normalizeSmartTemplateSchema());

    const PREVIEW_LOADING_STEPS = [
        t('templatesManager.previewLoadingSteps.analyzeFeedback'),
        t('templatesManager.previewLoadingSteps.fetchContext'),
        t('templatesManager.previewLoadingSteps.buildStructure'),
        t('templatesManager.previewLoadingSteps.determineSections'),
        t('templatesManager.previewLoadingSteps.generateSections'),
        t('templatesManager.previewLoadingSteps.writeContent'),
        t('templatesManager.previewLoadingSteps.refineContent'),
        t('templatesManager.previewLoadingSteps.applyStyle'),
        t('templatesManager.previewLoadingSteps.finishing'),
    ];

    // Template suggestions state
    const [suggestions, setSuggestions] = useState<TemplateSuggestion[]>([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [selectedSuggestionIds, setSelectedSuggestionIds] = useState<Set<number>>(new Set());
    const [applyingSuggestionId, setApplyingSuggestionId] = useState<number | null>(null);
    const [rejectingSuggestionId, setRejectingSuggestionId] = useState<number | null>(null);

    // Update component targeting state
    const [targetComponents, setTargetComponents] = useState<TemplateComponent[]>([]);
    const [useAutoMode, setUseAutoMode] = useState(false);
    const [finalizeProgressStep, setFinalizeProgressStep] = useState(0);
    const [finalizeProgress, setFinalizeProgress] = useState(0);

    const LOADING_MESSAGES = useMemo(() => [
        t('templatesManager.loadingMessages.readFeedback'),
        t('templatesManager.loadingMessages.analyzeRequirements'),
        t('templatesManager.loadingMessages.fetchContext'),
        t('templatesManager.loadingMessages.generateStructure'),
        t('templatesManager.loadingMessages.writeContent'),
        t('templatesManager.loadingMessages.finalizeTemplate')
    ], [t]);

    const FINALIZE_LOADING_STEPS = [
        t('templatesManager.finalizeLoadingSteps.setAlgorithm'),
        t('templatesManager.finalizeLoadingSteps.determineAiRole'),
        t('templatesManager.finalizeLoadingSteps.analyzeFeedback'),
        t('templatesManager.finalizeLoadingSteps.optimizeStructure'),
        t('templatesManager.finalizeLoadingSteps.setToneStyle'),
        t('templatesManager.finalizeLoadingSteps.configureSections'),
        t('templatesManager.finalizeLoadingSteps.determineOutputFormat'),
        t('templatesManager.finalizeLoadingSteps.validateTemplate'),
        t('templatesManager.finalizeLoadingSteps.qualityControl'),
        t('templatesManager.finalizeLoadingSteps.saveFinish'),
    ];

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isGenerating || isUpdating) {
            setProgress(0);
            setProgressMessage(LOADING_MESSAGES[0]);

            const startTime = Date.now();
            const duration = 30000; // 30 seconds estimated duration

            interval = setInterval(() => {
                const elapsed = Date.now() - startTime;
                const newProgress = Math.min((elapsed / duration) * 100, 95); // Cap at 95% until done

                setProgress(newProgress);

                // Cycle through messages based on progress
                const messageIndex = Math.min(
                    Math.floor((newProgress / 100) * LOADING_MESSAGES.length),
                    LOADING_MESSAGES.length - 1
                );
                setProgressMessage(LOADING_MESSAGES[messageIndex]);
            }, 200);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isGenerating, isUpdating, LOADING_MESSAGES]);

    // Preview progress animation
    useEffect(() => {
        if (!isGeneratingPreview) {
            setPreviewProgressStep(0);
            setPreviewProgress(0);
            return;
        }

        // Animate through steps (3 seconds per step)
        const stepInterval = setInterval(() => {
            setPreviewProgressStep(prev => {
                if (prev < PREVIEW_LOADING_STEPS.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 3000);

        // Animate progress bar smoothly (slower and stops at 85%)
        const progressInterval = setInterval(() => {
            setPreviewProgress(prev => {
                if (prev < 85) {
                    // Much slower increment
                    const increment = Math.max(0.3, (85 - prev) / 25);
                    return Math.min(85, prev + increment);
                }
                return prev;
            });
        }, 150);

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, [isGeneratingPreview, PREVIEW_LOADING_STEPS.length]);

    // Jump to 100% when preview data is ready
    useEffect(() => {
        if (previewData && isGeneratingPreview) {
            setPreviewProgress(100);
        }
    }, [previewData, isGeneratingPreview]);

    // Finalize progress animation
    useEffect(() => {
        if (!isGenerating) {
            setFinalizeProgressStep(0);
            setFinalizeProgress(0);
            return;
        }

        // Animate through steps every 4 seconds
        const stepInterval = setInterval(() => {
            setFinalizeProgressStep(prev => {
                if (prev < FINALIZE_LOADING_STEPS.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 4000);

        // Animate progress bar smoothly
        const progressInterval = setInterval(() => {
            setFinalizeProgress(prev => {
                if (prev < 92) {
                    const increment = Math.max(0.3, (92 - prev) / 20);
                    return Math.min(92, prev + increment);
                }
                return prev;
            });
        }, 100);

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, [isGenerating, FINALIZE_LOADING_STEPS.length]);

    // Fictive meeting generation progress animation
    useEffect(() => {
        if (!isGeneratingFictive) {
            setFictiveProgressStep(0);
            setFictiveProgress(0);
            return;
        }

        const stepInterval = setInterval(() => {
            setFictiveProgressStep(prev => {
                if (prev < FICTIVE_LOADING_STEPS.length - 1) {
                    return prev + 1;
                }
                return prev;
            });
        }, 3500);

        const progressInterval = setInterval(() => {
            setFictiveProgress(prev => {
                if (prev < 90) {
                    const increment = Math.max(0.4, (90 - prev) / 25);
                    return Math.min(90, prev + increment);
                }
                return prev;
            });
        }, 150);

        return () => {
            clearInterval(stepInterval);
            clearInterval(progressInterval);
        };
    }, [isGeneratingFictive, FICTIVE_LOADING_STEPS.length]);

    useEffect(() => {
        loadTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const urlSelectionDoneRef = useRef(false);
    useEffect(() => {
        if (urlSelectionDoneRef.current) return;
        if (!urlTemplateId || templates.length === 0) return;
        urlSelectionDoneRef.current = true;
        // De URL bevat de uuid; oude bookmarks nog de naam
        const match = templates.find(tpl => tpl.uuid === urlTemplateId)
            ?? templates.find(tpl => tpl.name === urlTemplateId);
        if (!match) {
            navigate('/templates', { replace: true });
            setError(t('templatesManager.errors.templateGone'));
            return;
        }
        if (selectedTemplateId !== match.id) {
            handleSelectTemplate(match);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTemplateId, templates]);

    useEffect(() => {
        if (!testSourceOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (testSourceDropdownRef.current && !testSourceDropdownRef.current.contains(e.target as Node)) {
                setTestSourceOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [testSourceOpen]);

    useEffect(() => {
        if (!confirmDialog) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setConfirmDialog(null);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [confirmDialog]);

    const loadTemplates = async () => {
        try {
            setError('');
            setLoading(true);
            const list = await api.getMeetingTypes();
            setTemplates(list);
        } catch (e) {
            setError(t('templatesManager.errors.loadTemplates'));
        } finally {
            setLoading(false);
        }
    };

    const editorTemplateRef = (template: MeetingType) => activeTemplateKeyRef.current ?? getTemplateRef(template);

    // Ga verder met de uuid uit de respons: die hoort bij het template dat de
    // gebruiker echt bewerkt (bv. een persoonlijke kopie van een standaardtemplate).
    // Een late respons voor een inmiddels gesloten template wordt genegeerd.
    // Geeft de sleutel terug waarmee de handler verder moet.
    const followTemplateUuid = (requestedRef: string, uuid: string | undefined): string => {
        if (!uuid || uuid === requestedRef || activeTemplateKeyRef.current !== requestedRef) return requestedRef;
        activeTemplateKeyRef.current = uuid;
        navigate(templateEditorPath(uuid), { replace: true });
        return uuid;
    };

    // 404: template bestaat niet (meer) of is niet zichtbaar → lijst verversen en
    // terug naar het overzicht. 403: melding tonen, niets opslaan.
    // Geeft true als de fout hiermee is afgehandeld.
    const handleTemplateAccessError = async (e: unknown): Promise<boolean> => {
        if (e instanceof TemplateNotFoundError) {
            navigate('/templates', { replace: true });
            activeTemplateKeyRef.current = null;
            setMode('none');
            setSelectedTemplateId(null);
            setCopyDialog(null);
            setCurrentDraft(null);
            setIsDraftDirty(false);
            handleCloseTestResult();
            handleCancelDiscard();
            await loadTemplates(); // wist eerdere meldingen, dus daarna pas de melding
            setSuccess('');
            setError(t('templatesManager.errors.templateGone'));
            return true;
        }
        if (e instanceof TemplatePermissionError) {
            setSuccess('');
            setError(t('templatesManager.errors.noPermission'));
            return true;
        }
        return false;
    };

    const handleGeneratePreview = async () => {
        setSuccess('');
        setError('');
        if (!generateFeedback.trim()) {
            setError(t('templatesManager.errors.feedbackRequired'));
            return;
        }
        try {
            setIsGeneratingPreview(true);
            // Save feedback for later finalization
            setSavedFeedbackForPreview(generateFeedback);
            setSavedRequirementsForPreview(requirements);

            const preview = await api.generateTemplatePreview(generateFeedback, requirements, 'gemini-flash', exampleDocument || undefined);
            setPreviewData(preview);

            let cleanContent = preview.content || '';

            // Verwijder eventuele trailing JSON blokken uit de content die daar niet horen
            const lastBraceIndex = cleanContent.lastIndexOf('{');
            if (lastBraceIndex !== -1 && cleanContent.trim().endsWith('}')) {
                const potentialJson = cleanContent.substring(lastBraceIndex);
                try {
                    JSON.parse(potentialJson);
                    // Als het valide JSON is aan het einde, strip het
                    cleanContent = cleanContent.substring(0, lastBraceIndex).trim();
                } catch (e) {
                    // Geen valide JSON of incompleet, laat staan
                }
            }

            setEditablePreviewContent(cleanContent);
            setEditablePreviewSchema(normalizeSmartTemplateSchema(preview.smartTemplateSchema));
            setPreviewTitle(preview.title || '');
            setPreviewDescription(preview.description || '');

            // Parse additional_settings en vul vooraf in
            if (preview.additionalSettings) {
                try {
                    const settings = typeof preview.additionalSettings === 'string'
                        ? JSON.parse(preview.additionalSettings)
                        : preview.additionalSettings;

                    const rawLength = settings.lengte ?? settings.aantal_woorden ?? settings.woorden ?? settings.length;
                    setPreviewFeedback({
                        // Ondersteun zowel doel_van_de_samenvatting als doelvande_samenvatting
                        goal: settings.doel_van_de_samenvatting || settings.doelvande_samenvatting || '',
                        perspective: settings.perspectief || '',
                        audience: settings.doelgroep || '',
                        style: settings.schrijfstijl || '',
                        tense: settings.tijd || '',
                        structure: '',
                        length: rawLength === undefined || rawLength === null ? '' : String(rawLength)
                    });
                } catch (e) {
                    logger.error('Failed to parse additional_settings:', e);
                    setPreviewFeedback({ goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' });
                }
            } else {
                setPreviewFeedback({ goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' });
            }

            setPreviewStep(1);
            // Keep modal open, don't close it automatically
            // User will click button to proceed
        } catch (e: any) {
            setError(e?.message || t('templatesManager.errors.generatePreview'));
            setIsGeneratingPreview(false);
        }
    };

    const handleFinalizeTemplate = async () => {
        setSuccess('');
        setError('');
        if (!previewData) {
            setError(t('templatesManager.errors.noPreviewData'));
            return;
        }
        if (hasSmartTemplateValidationErrors(editablePreviewSchema)) {
            setError(t('templatesManager.errors.smartFieldsInvalid'));
            return;
        }
        try {
            setIsGenerating(true);

            // Build enhanced feedback with step 2 input
            let enhancedFeedback = savedFeedbackForPreview;
            const step2Parts: string[] = [];
            if (previewFeedback.goal.trim()) step2Parts.push(t('templatesManager.feedbackParts.goal', { value: previewFeedback.goal.trim() }));
            if (previewFeedback.perspective.trim()) step2Parts.push(t('templatesManager.feedbackParts.perspective', { value: previewFeedback.perspective.trim() }));
            if (previewFeedback.audience.trim()) step2Parts.push(t('templatesManager.feedbackParts.audience', { value: previewFeedback.audience.trim() }));
            if (previewFeedback.style.trim()) step2Parts.push(t('templatesManager.feedbackParts.style', { value: previewFeedback.style.trim() }));
            if (previewFeedback.tense.trim()) step2Parts.push(t('templatesManager.feedbackParts.tense', { value: previewFeedback.tense.trim() }));
            if (previewFeedback.structure.trim()) step2Parts.push(t('templatesManager.feedbackParts.structure', { value: previewFeedback.structure.trim() }));
            if (step2Parts.length > 0) {
                enhancedFeedback += `\n\n${t('templatesManager.feedbackParts.additionalInstructions')}:\n- ${step2Parts.join('\n- ')}`;
            }

            // Use editablePreviewContent in case user edited the preview
            // Use title and description from modal if provided
            const finalPreviewData = {
                ...previewData,
                content: editablePreviewContent,
                title: previewTitle || previewData.title,
                description: previewDescription || previewData.description,
                smartTemplateSchema: buildSmartTemplateSchemaPayload(editablePreviewSchema),
            };

            // Voorkeuren (incl. lengte) gaan expliciet mee zodat ze deterministisch
            // in additional_settings van het nieuwe template belanden
            await api.generateTemplate(enhancedFeedback, savedRequirementsForPreview, 'gemini-flash', finalPreviewData, previewFeedback);
            // Reset all state
            setGenerateFeedback('');
            setRequirements('');
            setGenerateQA({ goal: '', sections: '', tone: '', terms: '' });
            setActiveGenerateQuestions([]);
            setPreviewData(null);
            setEditablePreviewContent('');
            setPreviewStep(1);
            setPreviewFeedback({ goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' });
            setPreviewTitle('');
            setPreviewDescription('');
            setEditablePreviewSchema(normalizeSmartTemplateSchema());
            setSavedFeedbackForPreview('');
            setSavedRequirementsForPreview('');
            await loadTemplates();
            setSuccess(t('templatesManager.success.templateGenerated'));
            setMode('none');
        } catch (e: any) {
            setError(e?.message || t('templatesManager.errors.finalizeTemplate'));
        } finally {
            setIsGenerating(false);
        }
    };

    const handleNextPreviewStep = () => {
        // Move from step 1 to step 2
        setPreviewStep(2);
    };

    const handlePreviousPreviewStep = () => {
        // Move back to step 1
        setPreviewStep(1);
    };

    const handleProceedFromModal = () => {
        // Close modal and show preview
        setIsGeneratingPreview(false);
        setMode('preview');
    };

    const handleCancelPreview = () => {
        setPreviewData(null);
        setEditablePreviewContent('');
        setEditablePreviewSchema(normalizeSmartTemplateSchema());
        setPreviewStep(1);
        setPreviewFeedback({ goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' });
        setPreviewTitle('');
        setPreviewDescription('');
        setMode('generate');
    };

    const handleUpdate = async () => {
        setSuccess('');
        setError('');
        if (!selectedTemplateId) {
            setError(t('templatesManager.errors.selectTemplateFirst'));
            return;
        }
        if (!updateFeedback.trim()) {
            setError(t('templatesManager.errors.feedbackRequired'));
            return;
        }
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) {
            setError(t('templatesManager.errors.templateNotFound'));
            return;
        }
        try {
            setIsUpdating(true);

            const options: { componentMode?: 'auto'; targetComponents?: TemplateComponent[] } = {};
            if (useAutoMode) {
                options.componentMode = 'auto';
            } else if (targetComponents.length > 0) {
                options.targetComponents = targetComponents;
            }

            // Verrijk feedback met aanvullende instellingen (zelfde shape als nieuw-template flow)
            let enrichedFeedback = updateFeedback.trim();
            const settingsParts: string[] = [];
            if (editAdditionalSettings.goal.trim()) settingsParts.push(t('templatesManager.feedbackParts.goal', { value: editAdditionalSettings.goal.trim() }));
            if (editAdditionalSettings.perspective.trim()) settingsParts.push(t('templatesManager.feedbackParts.perspective', { value: editAdditionalSettings.perspective.trim() }));
            if (editAdditionalSettings.audience.trim()) settingsParts.push(t('templatesManager.feedbackParts.audience', { value: editAdditionalSettings.audience.trim() }));
            if (editAdditionalSettings.style.trim()) settingsParts.push(t('templatesManager.feedbackParts.style', { value: editAdditionalSettings.style.trim() }));
            if (editAdditionalSettings.tense.trim()) settingsParts.push(t('templatesManager.feedbackParts.tense', { value: editAdditionalSettings.tense.trim() }));
            if (editAdditionalSettings.structure.trim()) settingsParts.push(t('templatesManager.feedbackParts.structure', { value: editAdditionalSettings.structure.trim() }));
            if (settingsParts.length > 0) {
                enrichedFeedback += `\n\n${t('templatesManager.feedbackParts.additionalSettingsTitle')}:\n- ${settingsParts.join('\n- ')}`;
            }

            await api.updateTemplateWithFeedback(selectedTemplateId, enrichedFeedback, 'gemini-flash',
                Object.keys(options).length > 0 ? options : undefined);

            setUpdateFeedback('');
            setTargetComponents([]);
            setUseAutoMode(false);

            // Ververs live template-inhoud in de editor en reset de draft-staat
            try {
                const templateRef = editorTemplateRef(template);
                const refreshed = await api.getTemplate(templateRef);
                if (refreshed) {
                    followTemplateUuid(templateRef, refreshed.uuid);
                    if (refreshed.content !== undefined) {
                        setEditableTemplateContent(refreshed.content);
                        setEditableTemplateSchema(normalizeSmartTemplateSchema(refreshed.smartTemplateSchema));
                    }
                    if (refreshed.title) setEditableTemplateTitle(refreshed.title);
                    if (refreshed.description !== undefined && refreshed.description !== null) {
                        setEditableTemplateDescription(refreshed.description);
                    }
                }
            } catch (e) {
                if (await handleTemplateAccessError(e)) return;
                logger.error('Fout bij verversen template na AI-update:', e);
            }
            setCurrentDraft(null);
            setSectionLengthLimits(null);
            setIsDraftDirty(false);
            await loadTemplates();
            setSuccess(t('templatesManager.success.templateUpdatedByAi'));
        } catch (e: any) {
            setError(e?.message || t('templatesManager.errors.updateTemplate'));
        } finally {
            setIsUpdating(false);
        }
    };

    const performDeleteTemplate = async (template: MeetingType) => {
        try {
            setLoading(true);
            await api.deleteTemplate(editorTemplateRef(template));
            setSuccess(t('templatesManager.success.templateDeleted'));
            navigate('/templates', { replace: true });
            setSelectedTemplateId(null);
            setMode('none');
            await loadTemplates();
        } catch (e: any) {
            setLoading(false);
            if (await handleTemplateAccessError(e)) return;
            setError(e?.message || t('templatesManager.errors.deleteTemplate'));
        }
    };

    const handleDelete = () => {
        if (!selectedTemplateId) return;

        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) {
            setError(t('templatesManager.errors.templateNotFound'));
            return;
        }

        const isShared = template.is_default || template.is_org_wide;
        setConfirmDialog({
            title: isShared ? t('templatesManager.delete.sharedTitle') : t('templatesManager.delete.title'),
            message: isShared
                ? t('templatesManager.delete.sharedMessage')
                : t('templatesManager.delete.message'),
            confirmLabel: t('common.delete'),
            confirmVariant: 'danger',
            onConfirm: () => performDeleteTemplate(template),
        });
    };

    const handleSaveMetadata = async () => {
        if (!selectedTemplateId) return;

        const originalTemplate = templates.find(t => t.id === selectedTemplateId);
        if (!originalTemplate) {
            setError(t('templatesManager.errors.templateNotFound'));
            return;
        }

        // Check if anything changed
        const titleChanged = editTitle.trim() !== originalTemplate.title;
        const descriptionChanged = editDescription.trim() !== (originalTemplate.description || '');

        if (!titleChanged && !descriptionChanged) {
            setError(t('templatesManager.errors.noChanges'));
            return;
        }

        try {
            setIsSavingMetadata(true);
            const updateData: { title?: string; description?: string } = {};
            if (titleChanged) updateData.title = editTitle.trim();
            if (descriptionChanged) updateData.description = editDescription.trim();

            await api.updateTemplateMetadata(selectedTemplateId, updateData);
            setSuccess(t('templatesManager.success.metadataUpdated'));
            navigate('/templates', { replace: true });
            setMode('none');
            setSelectedTemplateId(null);
            await loadTemplates();
        } catch (e: any) {
            setError(e?.message || t('templatesManager.errors.updateMetadata'));
        } finally {
            setIsSavingMetadata(false);
        }
    };

    const loadRecentFiles = async () => {
        setRecentFilesLoading(true);
        try {
            const recordings = await api.getRecordings();
            const recent = recordings
                .filter(r => r.status === ProcessingStatus.PROCESSED)
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, 5);
            setRecentFiles(recent);
        } catch (e) {
            logger.error('Fout bij laden recente opnames:', e);
        } finally {
            setRecentFilesLoading(false);
        }
    };

    // Groepeert templates voor de lijst: eigen → organisatie → standaard,
    // alfabetisch per groep, gefilterd op de zoekterm. Zelfde indeling als de
    // template-kiezer op de opnamedetailpagina.
    const getGroupedTemplates = () => {
        const query = listFilter.trim().toLowerCase();
        const matches = templates.filter(tpl =>
            !query ||
            tpl.title.toLowerCase().includes(query) ||
            (tpl.description || '').toLowerCase().includes(query)
        );
        const byTitle = (a: MeetingType, b: MeetingType) =>
            a.title.localeCompare(b.title, 'nl', { sensitivity: 'base' });
        return [
            { key: 'personal' as const, items: matches.filter(tpl => !tpl.is_default && !tpl.is_org_wide).sort(byTitle) },
            { key: 'org' as const, items: matches.filter(tpl => tpl.is_org_wide && !tpl.is_default).sort(byTitle) },
            { key: 'standard' as const, items: matches.filter(tpl => tpl.is_default).sort(byTitle) },
        ].filter(group => group.items.length > 0);
    };

    const handleSelectTemplate = async (t: MeetingType) => {
        if (t.is_default || t.is_org_wide) {
            setCopyDialog({ template: t, title: i18n.t('templatesManager.copyDialog.titleSuffix', { title: t.title }) });
            return;
        }
        const templateRef = getTemplateRef(t);
        activeTemplateKeyRef.current = templateRef;
        navigate(templateEditorPath(templateRef), { replace: true });
        setSelectedTemplateId(t.id);
        setSuccess('');
        setError('');
        setIsTesting(false);
        setTestContent('');
        setTestStatusMessage('');
        setTestError('');
        setSelectedTestOption('');
        setCachedFictiveTranscript(null);
        setUpdateFeedback('');
        setIsAdditionalSettingsOpen(false);

        let liveContent = t.content || '';
        let liveSchema: SmartTemplateSchema | null | undefined = t.smartTemplateSchema;
        let liveTitle = t.title || '';
        let liveDescription = t.description || '';
        let liveAdditionalSettings = t.additionalSettings;
        let liveRef = templateRef;
        try {
            const fullTemplate = await api.getTemplate(templateRef);
            if (fullTemplate) {
                liveRef = followTemplateUuid(templateRef, fullTemplate.uuid);
                if (fullTemplate.content) {
                    liveContent = fullTemplate.content;
                    liveSchema = fullTemplate.smartTemplateSchema;
                }
                if (fullTemplate.title) liveTitle = fullTemplate.title;
                if (fullTemplate.description !== undefined && fullTemplate.description !== null) {
                    liveDescription = fullTemplate.description;
                }
                if (fullTemplate.additionalSettings) liveAdditionalSettings = fullTemplate.additionalSettings;
            }
        } catch (e) {
            if (await handleTemplateAccessError(e)) return;
            logger.error('Fout bij laden template content:', e);
        }

        let latestDraft: TemplateDraft | null = null;
        let draftContent: string | undefined;
        let draftSchema: SmartTemplateSchema | null | undefined;
        let draftTitle: string | undefined;
        let draftDescription: string | undefined;
        let draftAdditionalSettings: typeof liveAdditionalSettings | undefined;
        try {
            const drafts = await api.getTemplateDrafts(liveRef, { limit: 1 });
            if (drafts.length > 0) {
                const fullDraft = await api.getTemplateDraft(liveRef, drafts[0].id);
                if (fullDraft) {
                    latestDraft = fullDraft;
                    draftContent = fullDraft.content;
                    draftSchema = fullDraft.smartTemplateSchema;
                    draftTitle = fullDraft.title;
                    draftDescription = fullDraft.description;
                    draftAdditionalSettings = fullDraft.additionalSettings;
                }
            }
        } catch (e) {
            if (await handleTemplateAccessError(e)) return;
            logger.error('Fout bij laden concepten:', e);
        }

        const resolvedSettings = draftAdditionalSettings ?? liveAdditionalSettings;
        setEditAdditionalSettings(resolvedSettings
            ? { goal: resolvedSettings.goal || '', perspective: resolvedSettings.perspective || '', audience: resolvedSettings.audience || '', style: resolvedSettings.style || '', tense: resolvedSettings.tense || '', structure: resolvedSettings.structure || '', length: resolvedSettings.length || '' }
            : { goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' });

        setCurrentDraft(latestDraft);
        setSectionLengthLimits(latestDraft?.sectionLengthLimits ?? null);
        setEditableTemplateContent(draftContent ?? liveContent);
        setEditableTemplateSchema(normalizeSmartTemplateSchema(draftSchema ?? liveSchema));
        setEditableTemplateTitle(draftTitle ?? liveTitle);
        setEditableTemplateDescription(draftDescription ?? liveDescription);
        setIsDraftDirty(false);
        const resolvedAdditionalSettings = resolvedSettings
            ? { goal: resolvedSettings.goal || '', perspective: resolvedSettings.perspective || '', audience: resolvedSettings.audience || '', style: resolvedSettings.style || '', tense: resolvedSettings.tense || '', structure: resolvedSettings.structure || '', length: resolvedSettings.length || '' }
            : { goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' };
        const initialSnapshot: EditorSnapshot = {
            content: draftContent ?? liveContent,
            title: draftTitle ?? liveTitle,
            description: draftDescription ?? liveDescription,
            schema: normalizeSmartTemplateSchema(draftSchema ?? liveSchema),
            additionalSettings: resolvedAdditionalSettings,
        };
        historyRef.current = [initialSnapshot];
        historyIndexRef.current = 0;
        pendingSnapshotRef.current = null;
        if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
        setCanUndo(false);
        setCanRedo(false);
        baselineSnapshotRef.current = initialSnapshot;
        setMode('update');

        loadRecentFiles();
        loadSuggestions(t.id);
    };

    const handleCopyTemplate = async () => {
        if (!copyDialog) return;
        const { template, title } = copyDialog;
        if (!title.trim()) {
            setError(t('templatesManager.errors.copyNameRequired'));
            return;
        }
        try {
            setIsCopying(true);
            setError('');
            const fullTemplate = await api.getTemplate(getTemplateRef(template));
            const content = fullTemplate?.content || template.content || '';
            const result = await api.generateTemplate(
                t('templatesManager.copyDialog.prompt', { title: title.trim() }),
                undefined,
                'gemini-flash',
                {
                    content,
                    title: title.trim(),
                    description: template.description,
                    smartTemplateSchema: fullTemplate?.smartTemplateSchema || template.smartTemplateSchema || null,
                }
            );
            setCopyDialog(null);
            const updatedList = await api.getMeetingTypes();
            setTemplates(updatedList);
            const newTemplate = updatedList.find(t => t.name === result.name);
            if (newTemplate) {
                handleSelectTemplate(newTemplate);
            }
        } catch (e: any) {
            if (await handleTemplateAccessError(e)) return;
            setError(e?.message || t('templatesManager.errors.copyTemplate'));
        } finally {
            setIsCopying(false);
        }
    };

    const loadSuggestions = async (templateId: number) => {
        setSuggestionsLoading(true);
        setSuggestions([]);
        setSelectedSuggestionIds(new Set());
        try {
            const result = await api.getTemplateSuggestions({ template_id: templateId, status: 'open', backfill: false });
            setSuggestions(result);
        } catch (e) {
            logger.error('Fout bij laden suggesties:', e);
        } finally {
            setSuggestionsLoading(false);
        }
    };

    const handleAcceptSuggestion = async (suggestion: TemplateSuggestion) => {
        setApplyingSuggestionId(suggestion.id);
        try {
            await api.acceptTemplateSuggestion(suggestion.id, {
                model: 'gemini-flash',
                component_mode: 'auto',
            });
            setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
            setSelectedSuggestionIds(prev => { const next = new Set(prev); next.delete(suggestion.id); return next; });
            if (selectedTemplateId) {
                const template = templates.find(t => t.id === selectedTemplateId);
                if (template) {
                    try {
                        const templateRef = editorTemplateRef(template);
                        const fullTemplate = await api.getTemplate(templateRef);
                        followTemplateUuid(templateRef, fullTemplate?.uuid);
                        if (fullTemplate?.content) {
                            setEditableTemplateContent(fullTemplate.content);
                            setEditableTemplateSchema(normalizeSmartTemplateSchema(fullTemplate.smartTemplateSchema));
                        }
                        if (fullTemplate?.title) setEditableTemplateTitle(fullTemplate.title);
                        if (fullTemplate?.description !== undefined) setEditableTemplateDescription(fullTemplate.description || '');
                    } catch (e) {
                        if (await handleTemplateAccessError(e)) return;
                    }
                }
            }
            setSuccess(t('templatesManager.success.suggestionApplied'));
            setTimeout(() => setSuccess(''), 3000);
        } catch (e: any) {
            setError(e?.message || t('templatesManager.errors.applySuggestion'));
            setTimeout(() => setError(''), 4000);
        } finally {
            setApplyingSuggestionId(null);
        }
    };

    const handleRejectSuggestion = async (suggestion: TemplateSuggestion) => {
        setRejectingSuggestionId(suggestion.id);
        try {
            await api.rejectTemplateSuggestion(suggestion.id);
            setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
            setSelectedSuggestionIds(prev => { const next = new Set(prev); next.delete(suggestion.id); return next; });
        } catch (e: any) {
            setError(e?.message || t('templatesManager.errors.rejectSuggestion'));
            setTimeout(() => setError(''), 4000);
        } finally {
            setRejectingSuggestionId(null);
        }
    };

    const handleAcceptSelectedSuggestions = async () => {
        const ids = Array.from(selectedSuggestionIds);
        for (let i = 0; i < ids.length; i++) {
            const suggestion = suggestions.find(s => s.id === ids[i]);
            if (suggestion) await handleAcceptSuggestion(suggestion);
        }
    };

    const handleRejectSelectedSuggestions = async () => {
        const ids = Array.from(selectedSuggestionIds);
        for (let i = 0; i < ids.length; i++) {
            const suggestion = suggestions.find(s => s.id === ids[i]);
            if (suggestion) await handleRejectSuggestion(suggestion);
        }
    };

    const toggleSuggestionSelection = (id: number) => {
        setSelectedSuggestionIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const snapshotsEqual = useCallback((a: EditorSnapshot, b: EditorSnapshot) => (
        a.content === b.content &&
        a.title === b.title &&
        a.description === b.description &&
        a.schema === b.schema &&
        a.additionalSettings.goal === b.additionalSettings.goal &&
        a.additionalSettings.perspective === b.additionalSettings.perspective &&
        a.additionalSettings.audience === b.additionalSettings.audience &&
        a.additionalSettings.style === b.additionalSettings.style &&
        a.additionalSettings.tense === b.additionalSettings.tense &&
        a.additionalSettings.structure === b.additionalSettings.structure &&
        a.additionalSettings.length === b.additionalSettings.length
    ), []);

    const updateHistoryButtons = useCallback(() => {
        setCanUndo(historyIndexRef.current > 0);
        setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
    }, []);

    const flushSnapshot = useCallback(() => {
        const snap = pendingSnapshotRef.current;
        if (!snap) return;
        pendingSnapshotRef.current = null;
        const history = historyRef.current;
        const idx = historyIndexRef.current;
        if (idx >= 0 && snapshotsEqual(history[idx], snap)) return;
        historyRef.current = [...history.slice(0, idx + 1), snap];
        historyIndexRef.current = historyRef.current.length - 1;
        updateHistoryButtons();
    }, [snapshotsEqual, updateHistoryButtons]);

    const pushUndoSnapshot = useCallback(() => {
        if (isApplyingHistoryRef.current) return;
        const current: EditorSnapshot = {
            content: editableTemplateContent,
            title: editableTemplateTitle,
            description: editableTemplateDescription,
            schema: editableTemplateSchema,
            additionalSettings: editAdditionalSettings,
        };
        pendingSnapshotRef.current = current;
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(flushSnapshot, 500);
    }, [editableTemplateContent, editableTemplateTitle, editableTemplateDescription, editableTemplateSchema, editAdditionalSettings, flushSnapshot]);

    const applySnapshot = (snap: EditorSnapshot) => {
        isApplyingHistoryRef.current = true;
        setEditableTemplateContent(snap.content);
        setEditableTemplateTitle(snap.title);
        setEditableTemplateDescription(snap.description);
        setEditableTemplateSchema(snap.schema);
        setEditAdditionalSettings(snap.additionalSettings);
        setTimeout(() => { isApplyingHistoryRef.current = false; }, 50);
    };

    const handleUndo = () => {
        if (historyIndexRef.current <= 0) return;
        if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
        // If there are pending changes, flush them first so we can undo back to them later
        if (pendingSnapshotRef.current) flushSnapshot();
        historyIndexRef.current -= 1;
        const snap = historyRef.current[historyIndexRef.current];
        applySnapshot(snap);
        updateHistoryButtons();
        const baseline = baselineSnapshotRef.current;
        if (baseline && snapshotsEqual(snap, baseline)) {
            setIsDraftDirty(false);
        } else {
            setIsDraftDirty(true);
        }
    };

    const handleRedo = () => {
        if (historyIndexRef.current >= historyRef.current.length - 1) return;
        if (debounceTimerRef.current) { clearTimeout(debounceTimerRef.current); debounceTimerRef.current = null; }
        historyIndexRef.current += 1;
        const snap = historyRef.current[historyIndexRef.current];
        applySnapshot(snap);
        updateHistoryButtons();
        setIsDraftDirty(true);
    };

    const handleEditorContentChange = (newContent: string) => {
        if (isApplyingHistoryRef.current) {
            // History is being applied — let the editor sync without recording a snapshot.
            setEditableTemplateContent(newContent);
            return;
        }
        if (newContent === editableTemplateContent) return; // no-op echo
        pushUndoSnapshot();
        setEditableTemplateContent(newContent);
        setIsDraftDirty(true);
    };

    const handleEditorFieldsChange = (fields: SmartTemplateField[]) => {
        if (isApplyingHistoryRef.current) {
            setEditableTemplateSchema(currentSchema => ({ ...currentSchema, fields }));
            return;
        }
        pushUndoSnapshot();
        setEditableTemplateSchema(currentSchema => ({ ...currentSchema, fields }));
        setIsDraftDirty(true);
    };

    const handleSaveDraft = async (): Promise<TemplateDraft | null> => {
        if (!selectedTemplateId) return null;
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) return null;
        if (hasSmartTemplateValidationErrors(editableTemplateSchema)) {
            setError(t('templatesManager.errors.smartFieldsInvalid'));
            return null;
        }
        if (!editableTemplateTitle.trim()) {
            setError(t('templatesManager.errors.titleRequired'));
            return null;
        }
        try {
            setIsSavingDraft(true);
            setError('');
            const templateRef = editorTemplateRef(template);
            const saved = await api.saveTemplateDraft(templateRef, {
                content: editableTemplateContent,
                title: editableTemplateTitle.trim(),
                description: editableTemplateDescription.trim(),
                smartTemplateSchema: buildSmartTemplateSchemaPayload(editableTemplateSchema),
                additionalSettings: editAdditionalSettings,
                revisionReason: t('templatesManager.revisionReason.draftSaved'),
            });
            followTemplateUuid(templateRef, saved.templateUuid);
            setCurrentDraft(saved);
            setSectionLengthLimits(saved.sectionLengthLimits ?? null);
            setIsDraftDirty(false);
            setSuccess(t('templatesManager.success.draftSaved'));
            return saved;
        } catch (e: any) {
            if (await handleTemplateAccessError(e)) return null;
            setError(e?.message || t('templatesManager.errors.saveDraft'));
            return null;
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleDiscardDraft = async () => {
        if (!selectedTemplateId) return;
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) return;
        if (!currentDraft && !isDraftDirty) return;

        setError('');
        try {
            const templateRef = editorTemplateRef(template);
            const refreshed = await api.getTemplate(templateRef);
            followTemplateUuid(templateRef, refreshed?.uuid);
            const snapshot: MeetingType = refreshed ?? {
                ...template,
                content: template.content ?? '',
            };
            setLiveCompareSnapshot(snapshot);
            setIsComparingDiscard(true);
            handleCloseTestResult();
        } catch (e: any) {
            if (await handleTemplateAccessError(e)) return;
            setError(e?.message || t('templatesManager.errors.loadLiveTemplate'));
        }
    };

    const handleCancelDiscard = () => {
        setIsComparingDiscard(false);
        setLiveCompareSnapshot(null);
    };

    const handleConfirmDiscard = async () => {
        if (!selectedTemplateId) return;
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) return;
        if (!currentDraft && !isDraftDirty) {
            handleCancelDiscard();
            return;
        }

        try {
            setIsDiscardingDraft(true);
            setError('');
            const templateRef = editorTemplateRef(template);
            if (currentDraft) {
                await api.deleteTemplateDraft(templateRef, currentDraft.id);
            }
            const snapshot = liveCompareSnapshot ?? (await api.getTemplate(templateRef));
            const liveContent = snapshot?.content ?? template.content ?? '';
            const liveSchema = snapshot?.smartTemplateSchema ?? template.smartTemplateSchema;
            const liveTitle = snapshot?.title ?? template.title ?? '';
            const liveDescription = snapshot?.description ?? template.description ?? '';
            setEditableTemplateContent(liveContent);
            setEditableTemplateSchema(normalizeSmartTemplateSchema(liveSchema));
            setEditableTemplateTitle(liveTitle);
            setEditableTemplateDescription(liveDescription);
            const wasDraft = !!currentDraft;
            setCurrentDraft(null);
            setSectionLengthLimits(null);
            setIsDraftDirty(false);
            setSuccess(wasDraft ? t('templatesManager.success.draftDiscarded') : t('templatesManager.success.changesDiscarded'));
            setIsComparingDiscard(false);
            setLiveCompareSnapshot(null);
        } catch (e: any) {
            if (await handleTemplateAccessError(e)) return;
            setError(e?.message || t('templatesManager.errors.discardDraft'));
        } finally {
            setIsDiscardingDraft(false);
        }
    };

    const performPublishDraft = async (template: MeetingType, revisionId: number) => {
        try {
            setIsPublishing(true);
            setError('');
            const templateRef = editorTemplateRef(template);
            const applied = await api.applyTemplateDraft(templateRef, revisionId, t('templatesManager.revisionReason.published'));
            followTemplateUuid(templateRef, applied.templateUuid);
            setSuccess(t('templatesManager.success.draftPublished'));
            setCurrentDraft(null);
            setIsDraftDirty(false);
            await loadTemplates();
        } catch (e: any) {
            if (await handleTemplateAccessError(e)) return;
            setError(e?.message || t('templatesManager.errors.publish'));
        } finally {
            setIsPublishing(false);
        }
    };

    const handlePublishDraft = () => {
        if (!selectedTemplateId) return;
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) return;

        const doPublish = async () => {
            let draftToPublish = currentDraft;
            if (!draftToPublish || isDraftDirty) {
                const saved = await handleSaveDraft();
                if (!saved) return;
                draftToPublish = saved;
            }
            await performPublishDraft(template, draftToPublish.id);
        };

        setConfirmDialog({
            title: t('templatesManager.publish.title'),
            message: t('templatesManager.publish.message'),
            confirmLabel: t('templatesManager.publish.confirmLabel'),
            confirmVariant: 'primary',
            onConfirm: doPublish,
        });
    };

    const handleStartTest = async () => {
        if (!selectedTemplateId) return;
        const template = templates.find(t => t.id === selectedTemplateId);
        if (!template) return;

        if (!selectedTestOption) {
            setTestError(t('templatesManager.errors.chooseTestOption'));
            return;
        }

        const isFictive = selectedTestOption === FICTIVE_OPTION_VALUE;

        setTestError('');

        let draftToUse = currentDraft;
        if (!draftToUse || isDraftDirty) {
            const saved = await handleSaveDraft();
            if (!saved) {
                return;
            }
            draftToUse = saved;
        }

        setIsTesting(true);
        setIsStreamingTest(true);
        setTestContent('');
        setTestStatusMessage(isFictive
            ? (cachedFictiveTranscript ? t('templatesManager.test.reuseCached') : t('templatesManager.test.generatingFictive'))
            : t('templatesManager.test.preparing'));

        let fictiveTranscript: string | null = isFictive ? cachedFictiveTranscript : null;

        if (isFictive && !fictiveTranscript) {
            try {
                setIsGeneratingFictive(true);
                const generated = await api.generateFictiveMeeting(editorTemplateRef(template), draftToUse.id, {
                    templateTestPrompt: t('templatesManager.fictive.prompt'),
                    language: 'nl',
                    model: 'auto',
                });
                fictiveTranscript = generated.transcript;
                setCachedFictiveTranscript(generated.transcript);
                setTestStatusMessage(t('templatesManager.test.fictiveReady'));
            } catch (e: any) {
                setTestError(e?.message || t('templatesManager.errors.generateFictiveMeeting'));
                setIsGeneratingFictive(false);
                setIsStreamingTest(false);
                return;
            } finally {
                setIsGeneratingFictive(false);
            }
        }

        if (!isFictive && !selectedTestOption) {
            setTestError(t('templatesManager.errors.noFileToTest'));
            setIsStreamingTest(false);
            return;
        }

        try {
            const stream = api.streamDraftTestSummary(editorTemplateRef(template), draftToUse.id, isFictive
                ? { testTranscript: fictiveTranscript || '' }
                : { fileId: selectedTestOption });
            for await (const update of stream) {
                if (update.statusMessage) setTestStatusMessage(update.statusMessage);
                if (update.content !== undefined) setTestContent(update.content);
                if (update.isComplete) break;
            }
        } catch (e: any) {
            setTestError(e?.message || t('templatesManager.errors.testTemplate'));
        } finally {
            setIsStreamingTest(false);
        }
    };

    const handleRegenerateFictive = () => {
        setCachedFictiveTranscript(null);
        handleStartTest();
    };

    const handleCloseTestResult = () => {
        setIsTesting(false);
        setTestContent('');
        setTestStatusMessage('');
        setTestError('');
    };

    return (
        <div className="min-h-screen bg-background pb-20">
            {/* In-app confirm dialog */}
            {confirmDialog && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setConfirmDialog(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmDialog.title}</h3>
                        <p className="text-sm text-gray-600 mb-6 whitespace-pre-line">{confirmDialog.message}</p>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmDialog(null)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                autoFocus
                                onClick={async () => {
                                    const cb = confirmDialog.onConfirm;
                                    setConfirmDialog(null);
                                    try {
                                        await cb();
                                    } catch (e) {
                                        logger.error('Confirm action failed:', e);
                                    }
                                }}
                                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white shadow-md hover:shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${confirmDialog.confirmVariant === 'danger'
                                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                    : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
                                    }`}
                            >
                                {confirmDialog.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Copy template dialog */}
            {copyDialog && (
                <div
                    className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => !isCopying && setCopyDialog(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{copyDialog.template.title}</h3>
                                <p className="text-sm text-gray-500">
                                    {copyDialog.template.is_default ? t('templatesManager.copyDialog.defaultTemplate') : t('templatesManager.copyDialog.orgTemplate')}
                                </p>
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            {copyDialog.template.is_default ? t('templatesManager.copyDialog.explanationDefault') : t('templatesManager.copyDialog.explanationOrg')}
                        </p>

                        {copyDialog.template.description && (
                            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                                <p className="text-sm text-gray-600">{copyDialog.template.description}</p>
                            </div>
                        )}

                        <label htmlFor="copy-title" className="block text-sm font-medium text-gray-700 mb-1">{t('templatesManager.copyDialog.nameLabel')}</label>
                        <input
                            id="copy-title"
                            type="text"
                            autoFocus
                            value={copyDialog.title}
                            onChange={(e) => setCopyDialog({ ...copyDialog, title: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !isCopying) handleCopyTemplate(); }}
                            disabled={isCopying}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 mb-5"
                        />

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setCopyDialog(null)}
                                disabled={isCopying}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleCopyTemplate}
                                disabled={isCopying || !copyDialog.title.trim()}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {isCopying ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('templatesManager.copyDialog.copying')}
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {t('templatesManager.copyDialog.copyAndEdit')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Finalization Overlay */}
            {isGenerating && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-fade-in">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                                <svg className="w-8 h-8 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('templatesManager.finalizeOverlay.title')}</h3>
                            <p className="text-sm text-gray-500">{t('templatesManager.finalizeOverlay.subtitle')}</p>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="font-medium text-primary-700 flex items-center">
                                    <span className="w-2 h-2 bg-primary-500 rounded-full mr-2 animate-pulse"></span>
                                    {FINALIZE_LOADING_STEPS[finalizeProgressStep]}
                                </span>
                                <span className="text-gray-500 tabular-nums">{Math.round(finalizeProgress)}%</span>
                            </div>

                            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-2.5 rounded-full transition-all duration-150 ease-out"
                                    style={{ width: `${finalizeProgress}%` }}
                                ></div>
                            </div>


                        </div>
                    </div>
                </div>
            )}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header Section */}
                <div className="mb-8 animate-fade-in">
                    {mode === 'update' && selectedTemplateId ? (
                        <div className="flex items-center justify-between gap-3">
                            <div className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 min-w-0">
                                <button
                                    type="button"
                                    onClick={() => { navigate('/templates', { replace: true }); setMode('none'); setSelectedTemplateId(null); setUpdateFeedback(''); handleCloseTestResult(); handleCancelDiscard(); }}
                                    className="hover:text-gray-900 transition-colors cursor-pointer"
                                >
                                    {t('templatesManager.header.breadcrumbTemplates')}
                                </button>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 flex-shrink-0">
                                    <polyline points="9 6 15 12 9 18"></polyline>
                                </svg>
                                <span className="text-gray-900 truncate">
                                    {templates.find(t => t.id === selectedTemplateId)?.title || editableTemplateTitle || t('templatesManager.header.fallbackTitle')}
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={isSavingDraft || isPublishing || isUpdating || isDiscardingDraft}
                                aria-label={t('templatesManager.header.deleteTemplateAria')}
                                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                    <path d="M10 11v6"></path>
                                    <path d="M14 11v6"></path>
                                </svg>
                                {t('templatesManager.header.deleteTemplate')}
                            </button>
                        </div>
                    ) : (
                        <>
                            <PageHeader title={t('templatesManager.header.title')} />
                            <p className="text-gray-600 mt-2">{t('templatesManager.header.subtitle')}</p>
                        </>
                    )}
                </div>

                {/* Status indicator (alleen voor generate/preview, niet voor update) */}
                {(mode === 'generate' || mode === 'preview') && (
                    <div className="mb-6 animate-fade-in inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                        {mode === 'generate' && t('templatesManager.status.generate')}
                        {mode === 'preview' && t('templatesManager.status.preview')}
                    </div>
                )}

                {/* Error en Success berichten */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 animate-fade-in">
                        <div className="flex">
                            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="ml-3 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                )}

                {success && (
                    <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 animate-fade-in">
                        <div className="flex">
                            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <p className="ml-3 text-sm text-green-700">{success}</p>
                        </div>
                    </div>
                )}

                {/* Startscherm */}
                {mode === 'none' && (
                    <div className="bg-white rounded-2xl shadow-soft p-8 mb-8 text-center animate-fade-in">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('templatesManager.start.title')}</h3>
                        <p className="text-gray-600 mb-8 max-w-md mx-auto">
                            {t('templatesManager.start.description')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <button
                                onClick={() => { navigate('/templates', { replace: true }); setMode('generate'); setSelectedTemplateId(null); setSuccess(''); setError(''); }}
                                className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-md transition-all hover:-translate-y-0.5"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                {t('templatesManager.start.newTemplate')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Nieuw template sectie */}
                {mode === 'generate' && (
                    <div className="bg-white rounded-2xl shadow-soft p-8 mb-8 animate-fade-in">
                        {/* Header with step indicator */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">{t('templatesManager.generate.stepTitle')}</h3>
                                    <p className="text-sm text-gray-600">{t('templatesManager.generate.stepSubtitle')}</p>
                                </div>
                            </div>
                        </div>



                        {/* Free text input */}
                        <div className="space-y-4 animate-fade-in">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.generate.descriptionLabel')}</label>
                                <textarea
                                    value={generateFeedback}
                                    onChange={(e) => setGenerateFeedback(e.target.value)}
                                    placeholder={t('templatesManager.generate.descriptionPlaceholder')}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    rows={6}
                                />
                            </div>
                        </div>


                        {/* Additional requirements */}
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.generate.requirementsLabel')}</label>
                            <input
                                value={requirements}
                                onChange={(e) => setRequirements(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                placeholder={t('templatesManager.generate.requirementsPlaceholder')}
                            />
                        </div>

                        {/* Example document upload */}
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.generate.exampleDocLabel')}</label>
                            <p className="text-xs text-gray-500 mb-3">{t('templatesManager.generate.exampleDocHint')}</p>

                            {!exampleDocument ? (
                                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                        <svg className="w-6 h-6 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                        </svg>
                                        <p className="text-sm text-gray-500">{t('templatesManager.generate.uploadCta')}</p>
                                        <p className="text-xs text-gray-400 mt-1">{t('templatesManager.generate.uploadFormats')}</p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".pdf,.docx,.doc,.txt"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setExampleDocument(e.target.files[0]);
                                            }
                                        }}
                                    />
                                </label>
                            ) : (
                                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-100">
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="text-sm font-medium text-primary-700">{exampleDocument.name}</span>
                                        <span className="text-xs text-primary-500 ml-2">({(exampleDocument.size / 1024).toFixed(1)} KB)</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setExampleDocument(null)}
                                        className="text-primary-600 hover:text-primary-800 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end space-x-3 mt-8">
                            {!isGenerating && !isGeneratingPreview && (
                                <button
                                    onClick={() => { setMode('none'); setGenerateFeedback(''); setRequirements(''); setGenerateQA({ goal: '', sections: '', tone: '', terms: '' }); setActiveGenerateQuestions([]); setExampleDocument(null); }}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    {t('common.cancel')}
                                </button>
                            )}

                            {isGenerating ? (
                                <div className="flex-1">
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="font-medium text-primary-700">{progressMessage}</span>
                                        <span className="text-gray-500">{Math.round(progress)}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                                        <div
                                            className="bg-primary-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                                            style={{ width: `${progress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            ) : !isGeneratingPreview && (
                                <button
                                    onClick={handleGeneratePreview}
                                    disabled={isGeneratingPreview}
                                    className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors shadow-md hover:shadow-lg"
                                >
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    {t('templatesManager.generate.nextStep')}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Preview Generation Modal */}
                {isGeneratingPreview && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-slide-up">
                            {/* Header */}
                            <div className="text-center mb-6">
                                <div className="w-16 h-16 mx-auto mb-4 bg-primary-100 rounded-full flex items-center justify-center">
                                    <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('templatesManager.previewModal.title')}</h3>
                            </div>

                            {/* Progress Bar */}
                            <div className="mb-6">
                                <div className="flex justify-between text-xs mb-2">
                                    <span className="text-gray-500">{PREVIEW_LOADING_STEPS[previewProgressStep]}</span>
                                    <span className="text-gray-700 font-medium tabular-nums">{Math.round(previewProgress)}%</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                                    <div
                                        className={`h-2.5 rounded-full transition-all duration-300 ease-out ${previewProgress >= 100
                                            ? 'bg-gradient-to-r from-green-500 to-green-600'
                                            : 'bg-gradient-to-r from-primary-500 to-primary-600'
                                            }`}
                                        style={{ width: `${previewProgress}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* Title Input (verkort wachttijd subjectief) */}
                            <div className="border-t border-gray-100 pt-6">
                                <p className="text-sm text-gray-600 mb-4">{t('templatesManager.previewModal.namePrompt')}</p>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.previewModal.templateNameLabel')}</label>
                                        <input
                                            type="text"
                                            value={previewTitle}
                                            onChange={(e) => setPreviewTitle(e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                            placeholder={t('templatesManager.previewModal.templateNamePlaceholder')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.previewModal.shortDescriptionLabel')}</label>
                                        <input
                                            type="text"
                                            value={previewDescription}
                                            onChange={(e) => setPreviewDescription(e.target.value)}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                            placeholder={t('templatesManager.previewModal.descriptionPlaceholder')}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Proceed Button (always visible, disabled until ready) */}
                            <div className="mt-6 pt-6 border-t border-gray-100">
                                <button
                                    onClick={handleProceedFromModal}
                                    disabled={!previewData}
                                    className={`w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${previewData
                                        ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                                        : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 disabled:hover:bg-primary-600'
                                        }`}
                                >
                                    {previewData ? (
                                        <>
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            {t('templatesManager.previewModal.readyNext')}
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            {t('templatesManager.previewModal.continueToPreview')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Preview sectie */}
                {mode === 'preview' && previewData && (
                    <div className="bg-white rounded-2xl shadow-soft p-8 mb-8 animate-fade-in">
                        {/* Header with step indicator */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">
                                        {previewStep === 1 ? t('templatesManager.preview.step1Title') : t('templatesManager.preview.step3Title')}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {previewStep === 1 ? t('templatesManager.preview.step1Subtitle') : t('templatesManager.preview.step3Subtitle')}
                                    </p>
                                </div>
                            </div>
                            {/* Step indicator 1-2-3 */}
                            <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-green-500 text-white">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div className="w-6 h-0.5 bg-green-300"></div>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${previewStep === 1 ? 'bg-primary-600 text-white' : 'bg-green-500 text-white'}`}>
                                    {previewStep === 1 ? '2' : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
                                </div>
                                <div className={`w-6 h-0.5 ${previewStep === 2 ? 'bg-green-300' : 'bg-gray-200'}`}></div>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${previewStep === 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
                            </div>
                        </div>

                        {/* Step 1: Editable preview content with WYSIWYG editor */}
                        {previewStep === 1 && (
                            <div className="mb-6">
                                {/* Editable Title and Description */}
                                <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 space-y-4 mb-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.templateNameLabel')}</label>
                                        <input
                                            type="text"
                                            value={previewTitle}
                                            onChange={(e) => setPreviewTitle(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                                            placeholder={t('templatesManager.preview.templateNamePlaceholder')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.descriptionLabel')}</label>
                                        <textarea
                                            value={previewDescription}
                                            onChange={(e) => setPreviewDescription(e.target.value)}
                                            rows={2}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white resize-none"
                                            placeholder={t('templatesManager.preview.descriptionPlaceholder')}
                                        />
                                    </div>
                                </div>

                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    {t('templatesManager.preview.structureLabel')}
                                </label>
                                <TemplateContentEditor
                                    value={editablePreviewContent}
                                    onChange={setEditablePreviewContent}
                                    smartFields={editablePreviewSchema.fields}
                                    onSmartFieldsChange={(fields) => setEditablePreviewSchema((currentSchema) => ({
                                        ...currentSchema,
                                        fields,
                                    }))}
                                    placeholder={t('templatesManager.preview.editorPlaceholder')}
                                    minHeight="350px"
                                    showSmartOverlay={showSmartOverlay}
                                />
                                <p className="mt-2 text-xs text-gray-500">
                                    {t('templatesManager.preview.structureHint')}
                                </p>
                            </div>
                        )}

                        {/* Step 2: Feedback form */}
                        {previewStep === 2 && (
                            <div className="space-y-5 mb-6">
                                <p className="text-sm text-gray-600 mb-4">
                                    {t('templatesManager.preview.refineHint')}
                                </p>

                                {/* Template Title and Description */}
                                <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 space-y-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.templateNameLabel')}</label>
                                        <input
                                            type="text"
                                            value={previewTitle}
                                            onChange={(e) => setPreviewTitle(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                                            placeholder={t('templatesManager.preview.templateNamePlaceholder')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.descriptionLabel')}</label>
                                        <textarea
                                            value={previewDescription}
                                            onChange={(e) => setPreviewDescription(e.target.value)}
                                            rows={2}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white resize-none"
                                            placeholder={t('templatesManager.preview.descriptionPlaceholder')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.goalLabel')}</label>
                                    <input
                                        type="text"
                                        value={previewFeedback.goal}
                                        onChange={(e) => setPreviewFeedback({ ...previewFeedback, goal: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                        placeholder={t('templatesManager.preview.goalPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.perspectiveLabel')}</label>
                                    <input
                                        type="text"
                                        value={previewFeedback.perspective}
                                        onChange={(e) => setPreviewFeedback({ ...previewFeedback, perspective: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                        placeholder={t('templatesManager.preview.perspectivePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.audienceLabel')}</label>
                                    <input
                                        type="text"
                                        value={previewFeedback.audience}
                                        onChange={(e) => setPreviewFeedback({ ...previewFeedback, audience: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                        placeholder={t('templatesManager.preview.audiencePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.tenseLabel')}</label>
                                    <select
                                        value={previewFeedback.tense}
                                        onChange={(e) => setPreviewFeedback({ ...previewFeedback, tense: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    >
                                        <option value="">{t('templatesManager.preview.tenseSelect')}</option>
                                        <option value="verleden tijd">{t('templatesManager.preview.tensePast')}</option>
                                        <option value="tegenwoordige tijd">{t('templatesManager.preview.tensePresent')}</option>
                                        <option value="toekomstige tijd">{t('templatesManager.preview.tenseFuture')}</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.styleLabel')}</label>
                                    <input
                                        type="text"
                                        value={previewFeedback.style}
                                        onChange={(e) => setPreviewFeedback({ ...previewFeedback, style: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                        placeholder={t('templatesManager.preview.stylePlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.preview.structureFieldLabel')}</label>
                                    <textarea
                                        value={previewFeedback.structure}
                                        onChange={(e) => setPreviewFeedback({ ...previewFeedback, structure: e.target.value })}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                        rows={3}
                                        placeholder={t('templatesManager.preview.structurePlaceholder')}
                                    />
                                </div>

                                <ReportLengthControl
                                    value={previewFeedback.length}
                                    onChange={(length) => setPreviewFeedback({ ...previewFeedback, length })}
                                />
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                            <button
                                onClick={previewStep === 1 ? handleCancelPreview : handlePreviousPreviewStep}
                                disabled={isGenerating}
                                className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                <svg className="w-4 h-4 mr-2 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                {previewStep === 1 ? t('templatesManager.preview.backToFeedback') : t('templatesManager.preview.backToStep1')}
                            </button>

                            <div className="flex space-x-3">
                                <button
                                    onClick={() => { setPreviewData(null); setEditablePreviewContent(''); setEditablePreviewSchema(normalizeSmartTemplateSchema()); setPreviewStep(1); setPreviewFeedback({ goal: '', perspective: '', audience: '', style: '', tense: '', structure: '', length: '' }); setMode('none'); setGenerateFeedback(''); setRequirements(''); }}
                                    disabled={isGenerating}
                                    className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    {t('common.cancel')}
                                </button>

                                {previewStep === 1 ? (
                                    <button
                                        onClick={handleNextPreviewStep}
                                        className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-md hover:shadow-lg"
                                    >
                                        {t('templatesManager.preview.nextStep')}
                                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                ) : isGenerating ? (
                                    <div className="flex items-center px-5 py-2.5">
                                        <svg className="animate-spin h-5 w-5 text-primary-600 mr-2" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-sm text-primary-700">{t('templatesManager.preview.saving')}</span>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleFinalizeTemplate}
                                        className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors shadow-md hover:shadow-lg"
                                    >
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t('templatesManager.preview.saveTemplate')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Template bijwerken sectie */}
                {mode === 'update' && selectedTemplateId && (() => {
                    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
                    const isReadOnlyTemplate = selectedTemplate?.is_default || selectedTemplate?.is_org_wide;
                    return (
                        <div className="mb-8 animate-fade-in">
                            {isReadOnlyTemplate && (
                                <p className="text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-lg mb-6 inline-block border border-primary-100">
                                    <span className="font-semibold mr-1">{t('templatesManager.update.readOnlyInfoLabel')}</span>
                                    {t('templatesManager.update.readOnlyInfo')}
                                </p>
                            )}

                            {/* Two-column layout (60/40 default, 50/50 during test or compare) */}
                            {isComparingDiscard ? (
                                <div className="space-y-4">
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                                        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        <div className="flex-1 text-sm">
                                            <p className="font-medium text-amber-900">{t('templatesManager.update.compareTitle')}</p>
                                            <p className="text-amber-800 mt-0.5">
                                                {t('templatesManager.update.compareDescription')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 lg:grid-cols-2">
                                        {/* LEFT: origineel (blijft) */}
                                        <div className="rounded-xl border border-gray-200 bg-white flex flex-col" style={{ minHeight: '500px' }}>
                                            <div className="flex items-center px-5 py-3 border-b border-gray-100 bg-emerald-50/40">
                                                <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="text-base font-semibold text-gray-900">{t('templatesManager.update.originalTitle')}</h4>
                                                    <p className="text-xs text-emerald-700">{t('templatesManager.update.originalSubtitle')}</p>
                                                </div>
                                            </div>
                                            <div className="flex-1 overflow-auto p-5">
                                                {liveCompareSnapshot && (
                                                    <>
                                                        <h3 className="text-xl font-semibold text-gray-900">{liveCompareSnapshot.title || t('templatesManager.update.noTitle')}</h3>
                                                        {liveCompareSnapshot.description && (
                                                            <p className="text-sm text-gray-600 mt-1 mb-4">{liveCompareSnapshot.description}</p>
                                                        )}
                                                        <div
                                                            className="notion-editor mt-3"
                                                            dangerouslySetInnerHTML={{ __html: markdownToHtml(liveCompareSnapshot.content || '') }}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* RIGHT: concept (wordt verwijderd) */}
                                        <div className="rounded-xl border-2 border-red-200 bg-red-50/20 flex flex-col" style={{ minHeight: '500px' }}>
                                            <div className="flex items-center px-5 py-3 border-b border-red-200 bg-red-50">
                                                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </div>
                                                <div className="min-w-0">
                                                    <h4 className="text-base font-semibold text-red-900">{t('templatesManager.update.currentChangesTitle')}</h4>
                                                    <p className="text-xs text-red-700">{t('templatesManager.update.currentChangesSubtitle')}</p>
                                                </div>
                                            </div>
                                            <div className="flex-1 overflow-auto p-5">
                                                <h3 className="text-xl font-semibold text-gray-900">{editableTemplateTitle || t('templatesManager.update.noTitle')}</h3>
                                                {editableTemplateDescription && (
                                                    <p className="text-sm text-gray-600 mt-1 mb-4">{editableTemplateDescription}</p>
                                                )}
                                                <div
                                                    className="notion-editor mt-3"
                                                    dangerouslySetInnerHTML={{ __html: markdownToHtml(editableTemplateContent || '') }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 pt-2">
                                        <button
                                            onClick={handleCancelDiscard}
                                            disabled={isDiscardingDraft}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                        >
                                            {t('templatesManager.update.backToEdit')}
                                        </button>
                                        <button
                                            onClick={handleConfirmDiscard}
                                            disabled={isDiscardingDraft}
                                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-md"
                                        >
                                            {isDiscardingDraft ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    {t('templatesManager.update.discarding')}
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    {t('templatesManager.update.discardChanges')}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={`grid gap-6 ${isTesting ? 'lg:grid-cols-2' : 'lg:grid-cols-[3fr_2fr]'}`}>
                                    {/* LEFT: editor + actions */}
                                    <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
                                        {currentDraft && !isDraftDirty && (
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                                                <span className="w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0"></span>
                                                {t('templatesManager.update.unpublishedDraft')}
                                            </div>
                                        )}
                                        {/* Title + description (binnen linker kolom) */}
                                        <div className="pb-4 border-b border-gray-100 space-y-3">
                                            <div>
                                                <label htmlFor="template-title" className="block text-xs font-medium text-gray-500 mb-1">{t('templatesManager.update.titleLabel')}</label>
                                                <input
                                                    id="template-title"
                                                    type="text"
                                                    value={editableTemplateTitle}
                                                    onChange={(e) => { if (isApplyingHistoryRef.current) return; pushUndoSnapshot(); setEditableTemplateTitle(e.target.value); setIsDraftDirty(true); }}
                                                    placeholder={t('templatesManager.update.titlePlaceholder')}
                                                    className="text-xl font-semibold text-gray-900 w-full bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-primary-500 focus:outline-none focus:ring-0 px-0 py-0.5 transition-colors placeholder-gray-300"
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="template-description" className="block text-xs font-medium text-gray-500 mb-1">{t('templatesManager.update.subtitleLabel')}</label>
                                                <textarea
                                                    id="template-description"
                                                    value={editableTemplateDescription}
                                                    onChange={(e) => { if (isApplyingHistoryRef.current) return; pushUndoSnapshot(); setEditableTemplateDescription(e.target.value); setIsDraftDirty(true); }}
                                                    placeholder={t('templatesManager.update.subtitlePlaceholder')}
                                                    rows={1}
                                                    className="text-sm text-gray-600 w-full bg-transparent border-0 border-b border-transparent hover:border-gray-200 focus:border-primary-500 focus:outline-none focus:ring-0 px-0 py-1 resize-none transition-colors placeholder-gray-300"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                {t('templatesManager.update.contentLabel')}
                                            </label>
                                            <p className="text-sm text-gray-500 mb-3">
                                                {t('templatesManager.update.contentHint')}
                                            </p>
                                            <TemplateContentEditor
                                                value={editableTemplateContent}
                                                onChange={handleEditorContentChange}
                                                smartFields={editableTemplateSchema.fields}
                                                onSmartFieldsChange={handleEditorFieldsChange}
                                                placeholder={t('templatesManager.update.editorPlaceholder')}
                                                minHeight="450px"
                                                showSmartOverlay={showSmartOverlay}
                                            />
                                            {sectionLengthLimits && sectionLengthLimits.limits.length > 0 && (
                                                <div className="mt-2 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                                                    <span className="font-medium text-gray-600">{t('templatesManager.update.sectionLimitsLabel')}: </span>
                                                    {[
                                                        ...sectionLengthLimits.limits.map((limit) => `${limit.section} — ${limit.description}`),
                                                        ...(sectionLengthLimits.fullyAnnotated && sectionLengthLimits.totalMaxWords
                                                            ? [t('templatesManager.update.sectionLimitsDerivedTotal', { words: Math.round(sectionLengthLimits.totalMaxWords * 1.15).toLocaleString(i18n.language) })]
                                                            : []),
                                                    ].join(' · ')}
                                                    {isDraftDirty && <span> · {t('templatesManager.update.sectionLimitsStale')}</span>}
                                                </div>
                                            )}
                                        </div>

                                        {/* Aanvullende instellingen (collapsible, gebruikt door Laat AI bijwerken) */}
                                        <div className="border border-gray-200 rounded-xl bg-white">
                                            <button
                                                type="button"
                                                onClick={() => setIsAdditionalSettingsOpen(v => !v)}
                                                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-xl"
                                            >
                                                <div>
                                                    <span className="text-sm font-medium text-gray-700">{t('templatesManager.update.additionalSettings')}</span>

                                                </div>
                                                <svg
                                                    className={`w-4 h-4 text-gray-500 transition-transform ${isAdditionalSettingsOpen ? 'rotate-180' : ''}`}
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                            {isAdditionalSettingsOpen && (
                                                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
                                                    <ReportLengthControl
                                                        value={editAdditionalSettings.length}
                                                        onChange={(length) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, length }); setIsDraftDirty(true); }}
                                                    />
                                                    <div className="border-t border-gray-100" />
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.goalLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={editAdditionalSettings.goal}
                                                            onChange={(e) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, goal: e.target.value }); setIsDraftDirty(true); }}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                                            placeholder={t('templatesManager.update.goalPlaceholder')}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.perspectiveLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={editAdditionalSettings.perspective}
                                                            onChange={(e) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, perspective: e.target.value }); setIsDraftDirty(true); }}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                                            placeholder={t('templatesManager.update.perspectivePlaceholder')}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.audienceLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={editAdditionalSettings.audience}
                                                            onChange={(e) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, audience: e.target.value }); setIsDraftDirty(true); }}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                                            placeholder={t('templatesManager.update.audiencePlaceholder')}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.tenseLabel')}</label>
                                                        <select
                                                            value={editAdditionalSettings.tense}
                                                            onChange={(e) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, tense: e.target.value }); setIsDraftDirty(true); }}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                                                        >
                                                            <option value="">{t('templatesManager.update.tenseSelect')}</option>
                                                            <option value="verleden tijd">{t('templatesManager.update.tensePast')}</option>
                                                            <option value="tegenwoordige tijd">{t('templatesManager.update.tensePresent')}</option>
                                                            <option value="toekomstige tijd">{t('templatesManager.update.tenseFuture')}</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.styleLabel')}</label>
                                                        <input
                                                            type="text"
                                                            value={editAdditionalSettings.style}
                                                            onChange={(e) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, style: e.target.value }); setIsDraftDirty(true); }}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                                            placeholder={t('templatesManager.update.stylePlaceholder')}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('templatesManager.update.structureLabel')}</label>
                                                        <textarea
                                                            value={editAdditionalSettings.structure}
                                                            onChange={(e) => { pushUndoSnapshot(); setEditAdditionalSettings({ ...editAdditionalSettings, structure: e.target.value }); setIsDraftDirty(true); }}
                                                            rows={2}
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                                                            placeholder={t('templatesManager.update.structurePlaceholder')}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                    </div>

                                    {/* RIGHT: AI + Test cards */}
                                    <div className="space-y-4">
                                        {/* Card: Template suggesties (hidden during test) */}
                                        {!isTesting && (
                                            <>
                                                {/* Card: Template suggesties */}
                                                {showTemplateSuggestions && (suggestions.length > 0 || suggestionsLoading) && (
                                                    <div className="group rounded-xl border border-gray-200 p-5 bg-white hover:border-amber-200 transition-colors" id="template-suggestions-card">
                                                        <div className="flex items-center mb-3">
                                                            <div className="w-9 h-9 bg-gray-100 group-hover:bg-amber-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-colors">
                                                                <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="text-base font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">{t('templatesManager.suggestions.title')}</h4>
                                                                <p className="text-xs text-gray-500">{t('templatesManager.suggestions.subtitle')}</p>
                                                            </div>
                                                            {suggestions.length > 0 && (
                                                                <span className="ml-2 inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-amber-700 bg-amber-100 rounded-full">
                                                                    {suggestions.length}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {suggestionsLoading ? (
                                                            <div className="flex items-center justify-center py-4">
                                                                <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <div className="space-y-2 max-h-72 overflow-y-auto">
                                                                    {suggestions.map(s => (
                                                                        <label
                                                                            key={s.id}
                                                                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedSuggestionIds.has(s.id)
                                                                                ? 'border-amber-300 bg-amber-50'
                                                                                : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                                                                                } ${applyingSuggestionId === s.id || rejectingSuggestionId === s.id ? 'opacity-60 pointer-events-none' : ''}`}
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={selectedSuggestionIds.has(s.id)}
                                                                                onChange={() => toggleSuggestionSelection(s.id)}
                                                                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                                                            />
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="text-sm text-gray-800 leading-snug">{s.normalized_instruction}</p>
                                                                                {(applyingSuggestionId === s.id) && (
                                                                                    <div className="mt-2 flex items-center text-xs text-amber-600">
                                                                                        <svg className="animate-spin mr-1.5 h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                        </svg>
                                                                                        {t('templatesManager.suggestions.applying')}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                                {selectedSuggestionIds.size > 0 && (
                                                                    <div className="flex gap-2 mt-3">
                                                                        <button
                                                                            onClick={handleAcceptSelectedSuggestions}
                                                                            disabled={applyingSuggestionId !== null}
                                                                            className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-amber-500 hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                        >
                                                                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                            {t('templatesManager.suggestions.apply', { count: selectedSuggestionIds.size })}
                                                                        </button>
                                                                        <button
                                                                            onClick={handleRejectSelectedSuggestions}
                                                                            disabled={rejectingSuggestionId !== null}
                                                                            className="inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-lg text-gray-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                                        >
                                                                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                            </svg>
                                                                            {t('templatesManager.suggestions.reject')}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Card: Laat AI bijwerken (altijd zichtbaar) */}
                                        <div className="group rounded-xl border border-[#09423a]/20 p-5 hover:border-[#09423a]/40 transition-colors" style={{ backgroundColor: '#E3E9E2' }}>
                                            <div className="flex items-center mb-3">
                                                <div className="w-9 h-9 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-colors" style={{ backgroundColor: 'rgba(9,66,58,0.1)' }}>
                                                    <svg className="w-5 h-5 transition-colors" style={{ color: '#09423a' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                    </svg>
                                                </div>
                                                <h4 className="text-base font-semibold text-gray-900 transition-colors">{t('templatesManager.ai.title')}</h4>
                                            </div>
                                            <p className="text-xs text-gray-500 mb-3">
                                                {t('templatesManager.ai.hint')}
                                            </p>
                                            <textarea
                                                value={updateFeedback}
                                                onChange={(e) => setUpdateFeedback(e.target.value)}
                                                placeholder={t('templatesManager.ai.placeholder')}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                                rows={5}
                                                disabled={isUpdating}
                                            />
                                            {isUpdating ? (
                                                <div className="mt-3">
                                                    <div className="flex justify-between text-xs mb-1">
                                                        <span className="font-medium text-primary-700">{progressMessage}</span>
                                                        <span className="text-gray-500">{Math.round(progress)}%</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                                        <div
                                                            className="bg-primary-600 h-2 rounded-full transition-all duration-300 ease-out"
                                                            style={{ width: `${progress}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={handleUpdate}
                                                    disabled={isUpdating || !updateFeedback.trim()}
                                                    className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#09423a]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    style={{ backgroundColor: '#09423a' }}
                                                >
                                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                    </svg>
                                                    {t('templatesManager.ai.updateByAi')}
                                                </button>
                                            )}
                                        </div>

                                        {/* Card: Template testen (hidden during test) */}
                                        {!isTesting && (
                                            <div className="group rounded-xl border border-gray-200 p-5 bg-white hover:border-emerald-200 transition-colors">
                                                <div className="flex items-center mb-3">
                                                    <div className="w-9 h-9 bg-gray-100 group-hover:bg-emerald-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0 transition-colors">
                                                        <svg className="w-5 h-5 text-gray-400 group-hover:text-emerald-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <h4 className="text-base font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">{t('templatesManager.testCard.title')}</h4>
                                                </div>

                                                <p className="text-xs text-gray-500 mb-3">
                                                    {t('templatesManager.testCard.hint')}
                                                </p>
                                                <label className="block text-xs font-medium text-gray-700 mb-1.5">{t('templatesManager.testCard.sourceLabel')}</label>
                                                <div className="relative" ref={testSourceDropdownRef}>
                                                    <button
                                                        type="button"
                                                        onClick={() => { if (!recentFilesLoading && !isSavingDraft && !isGeneratingFictive) setTestSourceOpen(v => !v); }}
                                                        disabled={recentFilesLoading || isSavingDraft || isGeneratingFictive}
                                                        className="w-full flex items-center pl-3 pr-10 py-2.5 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-800 bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                                    >
                                                        <span className="flex items-center gap-2 min-w-0">
                                                            {recentFilesLoading ? (
                                                                <svg className="animate-spin h-4 w-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            ) : selectedTestOption === FICTIVE_OPTION_VALUE ? (
                                                                <svg className="h-4 w-4 text-amber-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                                </svg>
                                                            ) : selectedTestOption ? (
                                                                <svg className="h-4 w-4 text-primary-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="h-4 w-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                </svg>
                                                            )}
                                                            <span className="truncate text-left">
                                                                {recentFilesLoading ? t('templatesManager.testCard.loadingRecordings')
                                                                    : selectedTestOption === FICTIVE_OPTION_VALUE ? t('templatesManager.testCard.withFictiveData')
                                                                        : selectedTestOption ? (recentFiles.find(f => f.id === selectedTestOption)?.name || t('templatesManager.testCard.recording'))
                                                                            : t('templatesManager.testCard.chooseSource')}
                                                            </span>
                                                        </span>
                                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
                                                            <svg className={`h-4 w-4 transition-transform ${testSourceOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </div>
                                                    </button>
                                                    {testSourceOpen && (
                                                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                                            <ul className="py-1 max-h-64 overflow-y-auto">
                                                                <li>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => { setSelectedTestOption(FICTIVE_OPTION_VALUE); setTestError(''); setTestSourceOpen(false); }}
                                                                        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors ${selectedTestOption === FICTIVE_OPTION_VALUE ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                                                    >
                                                                        <svg className="h-4 w-4 text-amber-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                                        </svg>
                                                                        <div>
                                                                            <div>{t('templatesManager.testCard.generateFictiveData')}</div>
                                                                            <div className="text-xs text-gray-400 font-normal">{t('templatesManager.testCard.generateFictiveDataHint')}</div>
                                                                        </div>
                                                                    </button>
                                                                </li>
                                                                {recentFiles.length > 0 && (
                                                                    <>
                                                                        <li className="px-3 pt-2.5 pb-1">
                                                                            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{t('templatesManager.testCard.recentRecordings')}</span>
                                                                        </li>
                                                                        {recentFiles.map(f => (
                                                                            <li key={f.id}>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => { setSelectedTestOption(f.id); setTestError(''); setTestSourceOpen(false); }}
                                                                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${selectedTestOption === f.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
                                                                                >
                                                                                    <svg className="h-4 w-4 text-primary-500 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                                                                    </svg>
                                                                                    <div className="min-w-0">
                                                                                        <div className="truncate">{f.name}</div>
                                                                                        {f.recordingDateTime && (
                                                                                            <div className="text-xs text-gray-400 font-normal">{new Date(f.recordingDateTime).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                                                                        )}
                                                                                    </div>
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>

                                                {testError && (
                                                    <p className="text-xs text-red-600 mt-2">{testError}</p>
                                                )}
                                                <button
                                                    onClick={handleStartTest}
                                                    disabled={
                                                        isSavingDraft ||
                                                        isGeneratingFictive ||
                                                        isTesting ||
                                                        !selectedTestOption
                                                    }
                                                    className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-gray-700 bg-gray-100 group-hover:text-white group-hover:bg-emerald-600 group-hover:hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {isSavingDraft ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            {t('templatesManager.testCard.savingDraft')}
                                                        </>
                                                    ) : isGeneratingFictive ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            {t('templatesManager.testCard.generatingMeeting')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                            </svg>
                                                            {t('templatesManager.testCard.startTest')}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                        {/* Streaming test output (visible during test) */}
                                        {isTesting && (
                                            <div className="rounded-xl border border-gray-200 bg-white flex flex-col" style={{ minHeight: '450px' }}>
                                                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                                                    <div className="flex items-center min-w-0">
                                                        <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                                                            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="text-base font-semibold text-gray-900 truncate">
                                                                {isGeneratingFictive ? t('templatesManager.testOutput.generatingFictiveTitle') : t('templatesManager.testOutput.testReportTitle')}
                                                            </h4>
                                                            {(isGeneratingFictive || testStatusMessage) && (
                                                                <p className="text-xs text-gray-500 truncate">
                                                                    {isGeneratingFictive ? FICTIVE_LOADING_STEPS[fictiveProgressStep] : testStatusMessage}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={handleCloseTestResult}
                                                        className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
                                                    >
                                                        {t('templatesManager.testOutput.close')}
                                                    </button>
                                                </div>
                                                <div className="flex-1 overflow-auto p-5">
                                                    {testError && (
                                                        <p className="text-sm text-red-600 mb-3">{testError}</p>
                                                    )}
                                                    {isGeneratingFictive ? (
                                                        <div className="flex flex-col items-center justify-center py-10 text-center">
                                                            <div className="w-14 h-14 mb-5 rounded-full bg-emerald-100 flex items-center justify-center">
                                                                <svg className="animate-spin h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            </div>
                                                            <p className="text-sm font-medium text-emerald-800 mb-1 flex items-center">
                                                                <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                                                                {FICTIVE_LOADING_STEPS[fictiveProgressStep]}
                                                            </p>
                                                            <p className="text-xs text-gray-500 mb-4">{t('templatesManager.testOutput.fictiveFollowUp')}</p>
                                                            <div className="w-full max-w-xs">
                                                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                                                    <span>{t('templatesManager.testOutput.progress')}</span>
                                                                    <span className="tabular-nums">{Math.round(fictiveProgress)}%</span>
                                                                </div>
                                                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                                                    <div
                                                                        className="bg-emerald-500 h-2 rounded-full transition-all duration-150 ease-out"
                                                                        style={{ width: `${fictiveProgress}%` }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : testContent ? (
                                                        <div
                                                            className="notion-editor"
                                                            dangerouslySetInnerHTML={{ __html: markdownToHtml(testContent) }}
                                                        />
                                                    ) : (
                                                        !testError && (
                                                            <div className="flex items-center text-sm text-gray-400">
                                                                <svg className="animate-spin h-4 w-4 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                {t('templatesManager.testOutput.waitingForResult')}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                                {!isStreamingTest && !isGeneratingFictive && (testContent || testError) && (
                                                    <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between gap-3 flex-wrap">
                                                        <div className="text-xs text-gray-500">
                                                            {testError
                                                                ? t('templatesManager.testOutput.testFailed')
                                                                : selectedTestOption === FICTIVE_OPTION_VALUE && cachedFictiveTranscript
                                                                    ? t('templatesManager.testOutput.testCompletedCached')
                                                                    : t('templatesManager.testOutput.testCompleted')}
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {selectedTestOption === FICTIVE_OPTION_VALUE && cachedFictiveTranscript && (
                                                                <button
                                                                    onClick={handleRegenerateFictive}
                                                                    className="text-xs text-gray-600 hover:text-gray-900 hover:underline transition-colors"
                                                                >
                                                                    {t('templatesManager.testOutput.newFictiveSession')}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={handleStartTest}
                                                                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                                            >
                                                                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                </svg>
                                                                {t('templatesManager.testOutput.rerunTest')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Floating save bar — when there are unsaved changes or an unpublished draft */}
                            {!isComparingDiscard && (isDraftDirty || currentDraft) && (
                                <div className="sticky bottom-6 z-50 mt-6">
                                    <div id="floating-save-bar" className="mx-auto w-fit rounded-2xl border border-gray-200 bg-white/95 px-5 py-3.5 shadow-lg backdrop-blur-sm">
                                        <div className="flex items-center justify-end gap-3">
                                            {isDraftDirty && (
                                                <>
                                                    <div className="inline-flex rounded-lg border border-gray-200" role="group" aria-label={t('templatesManager.saveBar.historyGroup')}>
                                                        <button
                                                            type="button"
                                                            onClick={handleUndo}
                                                            disabled={!canUndo}
                                                            className="inline-flex items-center justify-center w-9 h-9 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-l-lg border-r border-gray-200"
                                                            title={t('templatesManager.saveBar.undoTitle')}
                                                            aria-label={t('templatesManager.saveBar.undoAria')}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M3 7v6h6"></path>
                                                                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6.3 2.7L3 13"></path>
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={handleRedo}
                                                            disabled={!canRedo}
                                                            className="inline-flex items-center justify-center w-9 h-9 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-r-lg"
                                                            title={!canRedo ? t('templatesManager.saveBar.redoNothing') : t('templatesManager.saveBar.redoTitle')}
                                                            aria-label={t('templatesManager.saveBar.redoAria')}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M21 7v6h-6"></path>
                                                                <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6.3 2.7L21 13"></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    <span className="w-px h-6 bg-gray-200" aria-hidden="true"></span>
                                                </>
                                            )}

                                            {/* Secondary action: discard changes or the unpublished draft */}
                                            {(isDraftDirty || currentDraft) && (
                                                <button
                                                    type="button"
                                                    onClick={handleDiscardDraft}
                                                    disabled={isSavingDraft || isPublishing || isDiscardingDraft}
                                                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                                    title={t('templatesManager.saveBar.discardChangesTitle')}
                                                >
                                                    {t('templatesManager.saveBar.discardChanges')}
                                                </button>
                                            )}

                                            {/* Tertiary: save as draft without publishing */}
                                            {isDraftDirty && (
                                                <button
                                                    type="button"
                                                    onClick={handleSaveDraft}
                                                    disabled={isSavingDraft || isPublishing || isDiscardingDraft}
                                                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                                                    title={t('templatesManager.saveBar.saveDraftTitle')}
                                                >
                                                    {isSavingDraft ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-1.5 h-3.5 w-3.5 text-gray-500" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            {t('templatesManager.saveBar.saving')}
                                                        </>
                                                    ) : t('templatesManager.saveBar.saveAsDraft')}
                                                </button>
                                            )}

                                            {/* Primary action: always Publiceren */}
                                            <button
                                                type="button"
                                                onClick={handlePublishDraft}
                                                disabled={isSavingDraft || isPublishing || isDiscardingDraft}
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                            >
                                                {(isSavingDraft || isPublishing) ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        {t('templatesManager.saveBar.publishing')}
                                                    </>
                                                ) : t('templatesManager.saveBar.publish')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Metadata bewerken sectie */}
                {mode === 'editMetadata' && selectedTemplateId && (
                    <div className="bg-white rounded-2xl shadow-soft p-8 mb-8 animate-fade-in">
                        <div className="flex items-center mb-6">
                            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900">{t('templatesManager.editMetadata.title')}</h3>
                                <p className="text-sm text-gray-600">{t('templatesManager.editMetadata.subtitle')}</p>
                            </div>

                        </div>

                        <div className="space-y-6">
                            {/* Titel */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.editMetadata.titleLabel')}</label>
                                <input
                                    type="text"
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    placeholder={t('templatesManager.editMetadata.titlePlaceholder')}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                />
                            </div>

                            {/* Beschrijving */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('templatesManager.editMetadata.descriptionLabel')}</label>
                                <textarea
                                    value={editDescription}
                                    onChange={(e) => setEditDescription(e.target.value)}
                                    placeholder={t('templatesManager.editMetadata.descriptionPlaceholder')}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                                    rows={3}
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end space-x-3 mt-8">
                            <button
                                onClick={() => { navigate('/templates', { replace: true }); setMode('none'); setSelectedTemplateId(null); setEditTitle(''); setEditDescription(''); }}
                                disabled={isSavingMetadata}
                                className="px-5 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleSaveMetadata}
                                disabled={isSavingMetadata || !editTitle.trim()}
                                className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors shadow-md hover:shadow-lg"
                            >
                                {isSavingMetadata ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('templatesManager.editMetadata.saving')}
                                    </>
                                ) : (
                                    t('common.save')
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Templates lijst */}
                {mode === 'none' && (
                    <div className="bg-white rounded-2xl shadow-soft overflow-hidden animate-fade-in">
                        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                            <h3 className="text-base font-semibold text-gray-900">{t('templatesManager.list.title')}</h3>
                            <p className="text-sm text-gray-600">{t('templatesManager.list.subtitle')}</p>
                        </div>
                        <div className="p-6">
                            {templates.length === 0 ? (
                                <div className="text-center py-8">
                                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p className="text-gray-500">{t('templatesManager.list.empty')}</p>
                                </div>
                            ) : (
                                (() => {
                                    const groups = getGroupedTemplates();
                                    const showGroupLabels = groups.length > 1;
                                    return (
                                        <div className="meeting-types-picker">
                                            {templates.length > 7 && (
                                                <div className="meeting-type-search">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                        <circle cx="11" cy="11" r="8" />
                                                        <path d="M21 21l-4.35-4.35" />
                                                    </svg>
                                                    <input
                                                        type="text"
                                                        value={listFilter}
                                                        onChange={(e) => setListFilter(e.target.value)}
                                                        placeholder={t('templatesManager.list.searchPlaceholder')}
                                                    />
                                                </div>
                                            )}
                                            {groups.map((group) => (
                                                <div key={group.key} className="meeting-type-group">
                                                    {showGroupLabels && (
                                                        <h4 className="meeting-type-group-label">{t(`templatesManager.list.groups.${group.key}`)}</h4>
                                                    )}
                                                    <div className="meeting-types-list">
                                                        {group.items.map((template) => (
                                                            <button
                                                                type="button"
                                                                key={template.id}
                                                                className="meeting-type-button"
                                                                onClick={() => handleSelectTemplate(template)}
                                                            >
                                                                <span className="meeting-type-title">
                                                                    {template.title}
                                                                    {Boolean(template.smartTemplateSchema?.fields?.length) && (
                                                                        <span className="meeting-type-badge">{t('templatesManager.list.badgeSmart')}</span>
                                                                    )}
                                                                </span>
                                                                {template.description && <span className="meeting-type-desc">{template.description}</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            {groups.length === 0 && (
                                                <p className="meeting-types-no-results">{t('templatesManager.list.noResults')}</p>
                                            )}
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

export default TemplatesManager;
